/* eslint-disable perfectionist/sort-objects -- Keep translation locale columns in APP_LOCALES order. */
import { CATALOG_LOCALES, CatalogLocale } from '../locale';
import { defineDomainTranslations } from './defineDomainTranslations';

type TranslationRow = Record<CatalogLocale, string>;

const EDITOR_TRANSLATION_ROWS = {
  'editor.action.add': { en: 'Add', es: 'Añadir', pt: 'Adicionar', fr: 'Ajouter', it: 'Aggiungi', ru: 'Добавить' },
  'editor.action.applyChanges': {
    en: 'Apply changes',
    es: 'Aplicar cambios',
    pt: 'Aplicar alterações',
    fr: 'Appliquer les modifications',
    it: 'Applica modifiche',
    ru: 'Применить изменения',
  },
  'editor.action.back': { en: 'Back', es: 'Atrás', pt: 'Voltar', fr: 'Retour', it: 'Indietro', ru: 'Назад' },
  'editor.action.cancel': { en: 'Cancel', es: 'Cancelar', pt: 'Cancelar', fr: 'Annuler', it: 'Annulla', ru: 'Отмена' },
  'editor.action.choose': { en: 'Choose', es: 'Elegir', pt: 'Escolher', fr: 'Choisir', it: 'Scegli', ru: 'Выбрать' },
  'editor.action.continue': {
    en: 'Continue',
    es: 'Continuar',
    pt: 'Continuar',
    fr: 'Continuer',
    it: 'Continua',
    ru: 'Продолжить',
  },
  'editor.action.delete': {
    en: 'Delete',
    es: 'Eliminar',
    pt: 'Excluir',
    fr: 'Supprimer',
    it: 'Elimina',
    ru: 'Удалить',
  },
  'editor.action.download': {
    en: 'Download',
    es: 'Descargar',
    pt: 'Baixar',
    fr: 'Télécharger',
    it: 'Scarica',
    ru: 'Скачать',
  },
  'editor.action.edit': { en: 'Edit', es: 'Editar', pt: 'Editar', fr: 'Modifier', it: 'Modifica', ru: 'Изменить' },
  'editor.action.generate': { en: 'Generate', es: 'Generar', pt: 'Gerar', fr: 'Générer', it: 'Genera', ru: 'Создать' },
  'editor.action.generating': {
    en: 'Generating',
    es: 'Generando',
    pt: 'Gerando',
    fr: 'Génération',
    it: 'Generazione',
    ru: 'Создание',
  },
  'editor.action.getStarted': {
    en: 'Get started',
    es: 'Empezar',
    pt: 'Começar',
    fr: 'Commencer',
    it: 'Inizia',
    ru: 'Начать',
  },
  'editor.action.goBack': {
    en: 'Go back',
    es: 'Volver',
    pt: 'Voltar',
    fr: 'Retour',
    it: 'Torna indietro',
    ru: 'Назад',
  },
  'editor.action.goHome': {
    en: 'Go home',
    es: 'Ir al inicio',
    pt: 'Ir para o início',
    fr: 'Aller à l’accueil',
    it: 'Vai alla home',
    ru: 'На главную',
  },
  'editor.action.gotIt': {
    en: 'Got it',
    es: 'Entendido',
    pt: 'Entendi',
    fr: 'Compris',
    it: 'Ho capito',
    ru: 'Понятно',
  },
  'editor.action.learnMore': {
    en: 'Learn more',
    es: 'Más información',
    pt: 'Saiba mais',
    fr: 'En savoir plus',
    it: 'Scopri di più',
    ru: 'Подробнее',
  },
  'editor.action.import': {
    en: 'Import',
    es: 'Importar',
    pt: 'Importar',
    fr: 'Importer',
    it: 'Importa',
    ru: 'Импортировать',
  },
  'editor.action.next': { en: 'Next', es: 'Siguiente', pt: 'Próximo', fr: 'Suivant', it: 'Avanti', ru: 'Далее' },
  'editor.action.okay': { en: 'Okay', es: 'Entendido', pt: 'Certo', fr: 'D’accord', it: 'OK', ru: 'Хорошо' },
  'editor.action.previous': {
    en: 'Previous',
    es: 'Anterior',
    pt: 'Anterior',
    fr: 'Précédent',
    it: 'Indietro',
    ru: 'Назад',
  },
  'editor.action.rename': {
    en: 'Rename',
    es: 'Cambiar nombre',
    pt: 'Renomear',
    fr: 'Renommer',
    it: 'Rinomina',
    ru: 'Переименовать',
  },
  'editor.action.retranslate': {
    en: 'Retranslate',
    es: 'Volver a traducir',
    pt: 'Retraduzir',
    fr: 'Retraduire',
    it: 'Ritraduci',
    ru: 'Перевести заново',
  },
  'editor.action.save': { en: 'Save', es: 'Guardar', pt: 'Salvar', fr: 'Enregistrer', it: 'Salva', ru: 'Сохранить' },
  'editor.action.skip': { en: 'Skip', es: 'Omitir', pt: 'Ignorar', fr: 'Ignorer', it: 'Salta', ru: 'Пропустить' },
  'editor.action.submit': { en: 'Submit', es: 'Enviar', pt: 'Enviar', fr: 'Envoyer', it: 'Invia', ru: 'Отправить' },
  'editor.action.tryAgain': {
    en: 'Try again',
    es: 'Intentar de nuevo',
    pt: 'Tentar novamente',
    fr: 'Réessayer',
    it: 'Riprova',
    ru: 'Попробовать снова',
  },
  'editor.action.update': {
    en: 'Update',
    es: 'Actualizar',
    pt: 'Atualizar',
    fr: 'Mettre à jour',
    it: 'Aggiorna',
    ru: 'Обновить',
  },
  'editor.action.upgrade': {
    en: 'Upgrade',
    es: 'Mejorar el plan',
    pt: 'Fazer upgrade',
    fr: 'Passer à une offre supérieure',
    it: 'Passa a un piano superiore',
    ru: 'Перейти на другой тариф',
  },
  'editor.action.uploadVideo': {
    en: 'Upload video',
    es: 'Subir video',
    pt: 'Enviar vídeo',
    fr: 'Importer une vidéo',
    it: 'Carica video',
    ru: 'Загрузить видео',
  },
  'editor.bookDemo.action': {
    en: 'Book a Demo',
    es: 'Reservar una demo',
    pt: 'Agendar uma demonstração',
    fr: 'Réserver une démo',
    it: 'Prenota una demo',
    ru: 'Забронировать демо',
  },
  'editor.bookDemo.booked': {
    en: 'Demo booked',
    es: 'Demo reservada',
    pt: 'Demonstração agendada',
    fr: 'Démo réservée',
    it: 'Demo prenotata',
    ru: 'Демо забронировано',
  },
  'editor.accessibility.celebration': {
    en: 'Celebration',
    es: 'Celebración',
    pt: 'Comemoração',
    fr: 'Célébration',
    it: 'Festeggiamenti',
    ru: 'Праздник',
  },
  'editor.accessibility.desktop': {
    en: 'Desktop computer',
    es: 'Ordenador',
    pt: 'Computador de mesa',
    fr: 'Ordinateur',
    it: 'Computer desktop',
    ru: 'Компьютер',
  },
  'editor.accessibility.sparkles': {
    en: 'Sparkles',
    es: 'Destellos',
    pt: 'Brilhos',
    fr: 'Étincelles',
    it: 'Scintille',
    ru: 'Искры',
  },
  'editor.advancedTools.label': {
    en: 'Advanced tools',
    es: 'Herramientas avanzadas',
    pt: 'Ferramentas avançadas',
    fr: 'Outils avancés',
    it: 'Strumenti avanzati',
    ru: 'Расширенные инструменты',
  },
  'editor.advancedTools.none': { en: 'None', es: 'Ninguno', pt: 'Nenhum', fr: 'Aucun', it: 'Nessuno', ru: 'Нет' },
  'editor.advancedTools.off': {
    en: 'Off',
    es: 'Desactivado',
    pt: 'Desativado',
    fr: 'Désactivé',
    it: 'Disattivato',
    ru: 'Выкл.',
  },
  'editor.advancedTools.on': { en: 'On', es: 'Activado', pt: 'Ativado', fr: 'Activé', it: 'Attivato', ru: 'Вкл.' },
  'editor.advancedTools.prompting': {
    en: 'Translation prompting',
    es: 'Indicaciones de traducción',
    pt: 'Instruções de tradução',
    fr: 'Consignes de traduction',
    it: 'Prompt di traduzione',
    ru: 'Промпт для перевода',
  },
  'editor.advancedTools.srtFile': {
    en: 'SRT file',
    es: 'Archivo SRT',
    pt: 'Arquivo SRT',
    fr: 'Fichier SRT',
    it: 'File SRT',
    ru: 'Файл SRT',
  },
  'editor.audioTrack.open': {
    en: 'Open Editor',
    es: 'Abrir editor',
    pt: 'Abrir editor',
    fr: 'Ouvrir l’éditeur',
    it: 'Apri editor',
    ru: 'Открыть редактор',
  },
  'editor.audioTrack.zoomAria': {
    en: 'Audio zoom',
    es: 'Zoom de audio',
    pt: 'Escala do áudio',
    fr: 'Zoom de l’audio',
    it: 'Zoom audio',
    ru: 'Масштаб аудиодорожки',
  },
  'editor.attention.checkItOut': {
    en: 'Check it out ✨',
    es: '¡Míralo! ✨',
    pt: 'Confira ✨',
    fr: 'Jetez-y un œil ✨',
    it: 'Guardalo ✨',
    ru: 'Посмотрите ✨',
  },
  'editor.attention.oldSubtitle': {
    en: 'Apply changes to see the updates 👀',
    es: 'Aplica los cambios para ver las actualizaciones 👀',
    pt: 'Aplique as alterações para ver as atualizações 👀',
    fr: 'Appliquez les modifications pour voir les mises à jour 👀',
    it: 'Applica le modifiche per vedere gli aggiornamenti 👀',
    ru: 'Примените изменения, чтобы увидеть обновления 👀',
  },
  'editor.attention.oldTitle': {
    en: 'This is the old version!',
    es: '¡Esta es la versión anterior!',
    pt: 'Esta é a versão antiga!',
    fr: 'Il s’agit de l’ancienne version !',
    it: 'Questa è la versione precedente!',
    ru: 'Это старая версия!',
  },
  'editor.attention.readyTitle': {
    en: 'Your video is ready!',
    es: '¡Tu video está listo!',
    pt: 'Seu vídeo está pronto!',
    fr: 'Votre vidéo est prête !',
    it: 'Il video è pronto!',
    ru: 'Видео готово!',
  },
  'editor.editor.autodetect': {
    en: 'Autodetect',
    es: 'Detección automática',
    pt: 'Detecção automática',
    fr: 'Détection automatique',
    it: 'Rilevamento automatico',
    ru: 'Автоопределение',
  },
  'editor.editor.creatingSegment': {
    en: 'Creating new segment...',
    es: 'Creando un nuevo segmento...',
    pt: 'Criando novo segmento...',
    fr: 'Création d’un nouveau segment…',
    it: 'Creazione del nuovo segmento...',
    ru: 'Создаём новый сегмент…',
  },
  'editor.editor.infoLater': {
    en: 'Maybe later',
    es: 'Quizá más tarde',
    pt: 'Talvez mais tarde',
    fr: 'Peut-être plus tard',
    it: 'Forse più tardi',
    ru: 'Возможно, позже',
  },
  'editor.editor.infoSubtitle': {
    en: 'Explore our tutorial to learn how to effectively utilize this feature',
    es: 'Consulta nuestro tutorial para aprender a usar esta función',
    pt: 'Veja nosso tutorial para aprender a usar este recurso',
    fr: 'Consultez notre tutoriel pour utiliser efficacement cette fonctionnalité',
    it: 'Guarda il tutorial per usare al meglio questa funzione',
    ru: 'Посмотрите инструкцию, чтобы эффективно использовать эту функцию',
  },
  'editor.editor.infoTitle': {
    en: 'Translate your content to several languages easily',
    es: 'Traduce tu contenido fácilmente a varios idiomas',
    pt: 'Traduza seu conteúdo facilmente para vários idiomas',
    fr: 'Traduisez facilement votre contenu en plusieurs langues',
    it: 'Traduci facilmente i contenuti in più lingue',
    ru: 'Легко переводите контент на несколько языков',
  },
  'editor.editor.newLanguage': {
    en: 'New language',
    es: 'Nuevo idioma',
    pt: 'Novo idioma',
    fr: 'Nouvelle langue',
    it: 'Nuova lingua',
    ru: 'Новый язык',
  },
  'editor.editor.splitSegment': {
    en: 'Split segment',
    es: 'Dividir segmento',
    pt: 'Dividir segmento',
    fr: 'Scinder le segment',
    it: 'Dividi segmento',
    ru: 'Разделить сегмент',
  },
  'editor.editor.translate': {
    en: 'Translate',
    es: 'Traducir',
    pt: 'Traduzir',
    fr: 'Traduire',
    it: 'Traduci',
    ru: 'Перевести',
  },
  'editor.editor.voiceTooltipAlt': {
    en: 'Voice Settings',
    es: 'Ajustes de voz',
    pt: 'Configurações de voz',
    fr: 'Paramètres de voix',
    it: 'Impostazioni voce',
    ru: 'Настройки голоса',
  },
  'editor.editor.voiceTooltipSubtitle': {
    en: 'Adjust the expressiveness and similarity levels, then save it in your voice library',
    es: 'Ajusta la expresividad y la similitud y guarda la voz en tu biblioteca',
    pt: 'Ajuste a expressividade e a semelhança e salve na sua biblioteca de vozes',
    fr: 'Réglez l’expressivité et la similarité, puis enregistrez la voix dans votre bibliothèque',
    it: 'Regola espressività e somiglianza, poi salva la voce nella libreria',
    ru: 'Настройте выразительность и сходство, затем сохраните голос в библиотеку',
  },
  'editor.editor.voiceTooltipTitle': {
    en: 'Now you can customize your voice clone!',
    es: '¡Ahora puedes personalizar tu clon de voz!',
    pt: 'Agora você pode personalizar seu clone de voz!',
    fr: 'Vous pouvez désormais personnaliser votre clone vocal !',
    it: 'Ora puoi personalizzare il clone vocale!',
    ru: 'Теперь клон голоса можно настроить!',
  },
  'editor.dub.costFew': {
    en: "We'll dub this video and deduct {minutes} minutes from your account",
    es: 'Doblaremos este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Dublaremos este vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous doublerons cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Doppieremo il video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы озвучим это видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.dub.costMany': {
    en: "We'll dub this video and deduct {minutes} minutes from your account",
    es: 'Doblaremos este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Dublaremos este vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous doublerons cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Doppieremo il video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы озвучим это видео и спишем {minutes} минут с вашего аккаунта',
  },
  'editor.dub.costOne': {
    en: "We'll dub this video and deduct {minutes} minute from your account",
    es: 'Doblaremos este video y descontaremos {minutes} minuto de tu cuenta',
    pt: 'Dublaremos este vídeo e descontaremos {minutes} minuto da sua conta',
    fr: 'Nous doublerons cette vidéo et déduirons {minutes} minute de votre solde',
    it: 'Doppieremo il video e detrarremo {minutes} minuto dal tuo account',
    ru: 'Мы озвучим это видео и спишем {minutes} минуту с вашего аккаунта',
  },
  'editor.dub.costOther': {
    en: "We'll dub this video and deduct {minutes} minutes from your account",
    es: 'Doblaremos este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Dublaremos este vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous doublerons cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Doppieremo il video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы озвучим это видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.dub.button': {
    en: 'Dub Video',
    es: 'Doblar video',
    pt: 'Dublar vídeo',
    fr: 'Doubler la vidéo',
    it: 'Doppia il video',
    ru: 'Озвучить видео',
  },
  'editor.dub.criteriaSpeakers': {
    en: 'Speakers are accurately assigned',
    es: 'Los hablantes están asignados correctamente',
    pt: 'Os locutores foram atribuídos corretamente',
    fr: 'Les répliques sont attribuées aux bons locuteurs',
    it: 'I locutori sono assegnati correttamente',
    ru: 'Спикеры назначены правильно',
  },
  'editor.dub.criteriaText': {
    en: 'Text has been reviewed',
    es: 'El texto está revisado',
    pt: 'O texto foi revisado',
    fr: 'Le texte a été vérifié',
    it: 'Il testo è stato controllato',
    ru: 'Текст проверен',
  },
  'editor.dub.criteriaTimestamps': {
    en: 'Timestamps are correctly placed',
    es: 'Las marcas de tiempo están colocadas correctamente',
    pt: 'As marcações de tempo estão posicionadas corretamente',
    fr: 'Les horodatages sont correctement placés',
    it: 'I timestamp sono posizionati correttamente',
    ru: 'Таймкоды расставлены правильно',
  },
  'editor.dub.dontShow': {
    en: "Don't show this again",
    es: 'No volver a mostrar',
    pt: 'Não mostrar novamente',
    fr: 'Ne plus afficher',
    it: 'Non mostrare più',
    ru: 'Больше не показывать',
  },
  'editor.dub.deleteAll': {
    en: 'Delete all ({count})',
    es: 'Eliminar todos ({count})',
    pt: 'Excluir todos ({count})',
    fr: 'Tout supprimer ({count})',
    it: 'Elimina tutti ({count})',
    ru: 'Удалить все ({count})',
  },
  'editor.dub.emptySegments': {
    en: 'Fill in or delete empty segments to dub the video.',
    es: 'Completa o elimina los segmentos vacíos para doblar el video.',
    pt: 'Preencha ou exclua os segmentos vazios para dublar o vídeo.',
    fr: 'Renseignez ou supprimez les segments vides pour doubler la vidéo.',
    it: 'Compila o elimina i segmenti vuoti per doppiare il video.',
    ru: 'Заполните или удалите пустые сегменты, чтобы озвучить видео.',
  },
  'editor.dub.free': { en: 'Free', es: 'Gratis', pt: 'Grátis', fr: 'Gratuit', it: 'Gratis', ru: 'Бесплатно' },
  'editor.dub.minutesShort': {
    en: '{minutes} min',
    es: '{minutes} min',
    pt: '{minutes} min',
    fr: '{minutes} min',
    it: '{minutes} min',
    ru: '{minutes} мин',
  },
  'editor.dub.notEnoughSpeech': {
    en: 'Not enough speech to clone the voice for {speaker}. Edit the speaker assignment or use recommended presets.',
    es: 'No hay suficiente audio de voz para clonar la voz de {speaker}. Cambia la asignación del hablante o usa las voces predefinidas recomendadas.',
    pt: 'Não há fala suficiente para clonar a voz de {speaker}. Altere a atribuição do falante ou use as predefinições recomendadas.',
    fr: 'La durée de parole est insuffisante pour cloner la voix de {speaker}. Modifiez l’attribution du locuteur ou utilisez les voix prédéfinies recommandées.',
    it: 'Il parlato non è sufficiente per clonare la voce di {speaker}. Modifica l’assegnazione del locutore o usa i preset consigliati.',
    ru: 'Недостаточно речи, чтобы клонировать голос спикера {speaker}. Измените привязку реплик к спикеру или выберите один из рекомендованных готовых голосов.',
  },
  'editor.dub.subtitle': {
    en: 'To achieve better dubbing results, please ensure your script is ready:',
    es: 'Para obtener mejores resultados de doblaje, asegúrate de que el guion esté listo:',
    pt: 'Para obter melhores resultados de dublagem, verifique se o roteiro está pronto:',
    fr: 'Pour obtenir un meilleur doublage, vérifiez que votre script est prêt :',
    it: 'Per ottenere un doppiaggio migliore, assicurati che il copione sia pronto:',
    ru: 'Чтобы получить лучший результат озвучки, убедитесь, что сценарий готов:',
  },
  'editor.dub.timeCost': {
    en: 'Time cost:',
    es: 'Coste en minutos:',
    pt: 'Custo:',
    fr: 'Coût en minutes :',
    it: 'Costo in minuti:',
    ru: 'Стоимость:',
  },
  'editor.dub.tooltipEdit': {
    en: 'Finish editing before dubbing',
    es: 'Termina de editar antes de doblar',
    pt: 'Conclua a edição antes de dublar',
    fr: 'Terminez les modifications avant de lancer le doublage',
    it: 'Termina le modifiche prima di avviare il doppiaggio',
    ru: 'Завершите редактирование перед озвучкой',
  },
  'editor.dub.tooltipNoChanges': {
    en: 'Make changes to dub the video again',
    es: 'Haz cambios para volver a doblar el video',
    pt: 'Faça alterações para dublar o vídeo novamente',
    fr: 'Modifiez le projet pour doubler à nouveau la vidéo',
    it: 'Apporta modifiche per doppiare di nuovo il video',
    ru: 'Внесите изменения, чтобы озвучить видео заново',
  },
  'editor.dub.tooltipProcessing': {
    en: 'Wait until the data is saved',
    es: 'Espera a que se guarden los datos',
    pt: 'Aguarde até que os dados sejam salvos',
    fr: 'Attendez que les données soient enregistrées',
    it: 'Attendi che i dati vengano salvati',
    ru: 'Дождитесь сохранения данных',
  },
  'editor.dub.tooltipUnavailableDemo': {
    en: 'Unavailable for demo project',
    es: 'No disponible para el proyecto de demostración',
    pt: 'Indisponível para projeto de demonstração',
    fr: 'Indisponible pour le projet de démonstration',
    it: 'Non disponibile per il progetto demo',
    ru: 'Недоступно в демо-проекте',
  },
  'editor.dub.tooltipVoiceSuggest': {
    en: 'Wait until voice selection is complete',
    es: 'Espera a que termine la selección de voces',
    pt: 'Aguarde até a seleção de vozes terminar',
    fr: 'Attendez la fin de la sélection des voix',
    it: 'Attendi il completamento della selezione delle voci',
    ru: 'Дождитесь завершения подбора голосов',
  },
  'editor.dub.unlockUnlimited': {
    en: 'Unlock unlimited edits',
    es: 'Desbloquear ediciones ilimitadas',
    pt: 'Desbloquear edições ilimitadas',
    fr: 'Débloquer les modifications illimitées',
    it: 'Sblocca modifiche illimitate',
    ru: 'Снять ограничение на редактирование',
  },
  'editor.dub.upgradeApplyChanges': {
    en: 'Upgrade to apply changes',
    es: 'Mejora tu plan para aplicar los cambios',
    pt: 'Faça upgrade para aplicar as alterações',
    fr: 'Passez à une offre supérieure pour appliquer les modifications',
    it: 'Passa a un piano superiore per applicare le modifiche',
    ru: 'Перейдите на более высокий тариф, чтобы применить изменения',
  },
  'editor.dub.usePresets': {
    en: 'Use presets ({count})',
    es: 'Usar voces predefinidas ({count})',
    pt: 'Usar predefinições ({count})',
    fr: 'Utiliser les voix prédéfinies ({count})',
    it: 'Usa preset ({count})',
    ru: 'Выбрать готовые голоса ({count})',
  },
  'editor.error.determineSpeakers': {
    en: 'Failed to determine speakers',
    es: 'No se pudieron detectar los hablantes',
    pt: 'Falha ao identificar os falantes',
    fr: 'Échec de la détection des locuteurs',
    it: 'Rilevamento dei parlanti non riuscito',
    ru: 'Не удалось определить спикеров',
  },
  'editor.error.dubbingSubtitle': {
    en: "If that doesn't help, please contact our support team, and we'll be happy to assist you.",
    es: 'Si eso no ayuda, contacta con nuestro equipo de soporte; estaremos encantados de ayudarte.',
    pt: 'Se isso não ajudar, fale com a nossa equipe de suporte. Teremos prazer em ajudar.',
    fr: 'Si cela ne suffit pas, contactez notre équipe d’assistance, qui se fera un plaisir de vous aider.',
    it: 'Se non basta, contatta il nostro team di assistenza: saremo felici di aiutarti.',
    ru: 'Если это не поможет, обратитесь в поддержку — мы обязательно поможем.',
  },
  'editor.error.dubbingTitle': {
    en: 'Dubbing failed. Your minutes were not deducted, so you can dub again.',
    es: 'Error de doblaje. No te preocupes: tus minutos no han cambiado y puedes volver a doblar.',
    pt: 'Ocorreu um erro na dublagem. Seus minutos não foram alterados; você pode dublar novamente.',
    fr: 'Échec du doublage. Vos minutes n’ont pas été déduites, vous pouvez donc relancer le doublage.',
    it: 'Si è verificato un errore nel doppiaggio. I minuti non sono cambiati e puoi riprovare.',
    ru: 'Ошибка при озвучке. Минуты не списались — можно запустить озвучку снова.',
  },
  'editor.error.generic': {
    en: 'Something went wrong',
    es: 'Algo salió mal',
    pt: 'Algo deu errado',
    fr: 'Une erreur est survenue',
    it: 'Qualcosa è andato storto',
    ru: 'Что-то пошло не так',
  },
  'editor.error.genericRetry': {
    en: 'Please try again.',
    es: 'Algo salió mal. Inténtalo de nuevo',
    pt: 'Algo deu errado. Tente novamente',
    fr: 'Une erreur est survenue. Réessayez',
    it: 'Qualcosa è andato storto. Riprova',
    ru: 'Попробуйте снова.',
  },
  'editor.error.noSoundSubtitle': {
    en: 'Please upload a video with speech',
    es: 'Sube un video con voz',
    pt: 'Envie um vídeo com fala',
    fr: 'Importez une vidéo contenant de la parole',
    it: 'Carica un video con del parlato',
    ru: 'Загрузите видео с речью',
  },
  'editor.error.noSoundTitle': {
    en: 'Video has no sound',
    es: 'El video no tiene sonido',
    pt: 'O vídeo não tem som',
    fr: 'La vidéo n’a pas de son',
    it: 'Il video non ha audio',
    ru: 'В видео нет звука',
  },
  'editor.error.noSpeechSubtitle': {
    en: 'Please provide a video with speech',
    es: 'Proporciona un video con voz',
    pt: 'Forneça um vídeo com fala',
    fr: 'Fournissez une vidéo contenant de la parole',
    it: 'Fornisci un video con del parlato',
    ru: 'Выберите видео с речью',
  },
  'editor.error.noSpeechTitle': {
    en: 'No speech in video',
    es: 'No hay voz en el video',
    pt: 'Não há fala no vídeo',
    fr: 'Aucune parole dans la vidéo',
    it: 'Nessun parlato nel video',
    ru: 'В видео нет речи',
  },
  'editor.error.pageMissing': {
    en: 'Page not found',
    es: 'Página no encontrada',
    pt: 'Página não encontrada',
    fr: 'Page introuvable',
    it: 'Pagina non trovata',
    ru: 'Страница не найдена',
  },
  'editor.error.pageMissingSubtitle': {
    en: 'The page you are looking for doesn’t exist.',
    es: 'La página que buscas no existe.',
    pt: 'A página que você procura não existe.',
    fr: 'La page que vous recherchez n’existe pas.',
    it: 'La pagina che cerchi non esiste.',
    ru: 'Страница, которую вы ищете, не существует.',
  },
  'editor.error.refresh': {
    en: 'Refresh page',
    es: 'Actualizar página',
    pt: 'Atualizar página',
    fr: 'Actualiser la page',
    it: 'Aggiorna pagina',
    ru: 'Обновить страницу',
  },
  'editor.error.separateBackground': {
    en: 'Failed to separate the background audio',
    es: 'No se pudo separar el audio de fondo',
    pt: 'Falha ao separar o áudio de fundo',
    fr: 'Échec de la séparation de l’audio d’arrière-plan',
    it: 'Separazione dell’audio di sottofondo non riuscita',
    ru: 'Не удалось отделить фоновое аудио',
  },
  'editor.error.server': {
    en: 'Server error. Please try again later',
    es: 'Error del servidor. Inténtalo de nuevo más tarde',
    pt: 'Erro no servidor. Tente novamente mais tarde',
    fr: 'Erreur du serveur. Réessayez plus tard',
    it: 'Errore del server. Riprova più tardi',
    ru: 'Ошибка сервера. Попробуйте позже',
  },
  'editor.error.transcribeSegments': {
    en: 'Failed to transcribe segments',
    es: 'No se pudieron transcribir los segmentos',
    pt: 'Falha ao transcrever os segmentos',
    fr: 'Échec de la transcription des segments',
    it: 'Trascrizione dei segmenti non riuscita',
    ru: 'Не удалось расшифровать сегменты',
  },
  'editor.error.transcription': {
    en: 'Transcription failed',
    es: 'Error de transcripción',
    pt: 'Falha na transcrição',
    fr: 'Échec de la transcription',
    it: 'Trascrizione non riuscita',
    ru: 'Ошибка транскрипции',
  },
  'editor.error.translation': {
    en: 'Translation failed',
    es: 'Error de traducción',
    pt: 'Falha na tradução',
    fr: 'Échec de la traduction',
    it: 'Traduzione non riuscita',
    ru: 'Ошибка перевода',
  },
  'editor.error.tryRefresh': {
    en: 'Please refresh the page or try again later.',
    es: 'Actualiza la página o inténtalo de nuevo más tarde.',
    pt: 'Atualize a página ou tente novamente mais tarde.',
    fr: 'Actualisez la page ou réessayez plus tard.',
    it: 'Aggiorna la pagina o riprova più tardi.',
    ru: 'Обновите страницу или попробуйте позже.',
  },
  'editor.error.upload': {
    en: 'Upload failed',
    es: 'Error de carga',
    pt: 'Falha no envio',
    fr: 'Échec de l’importation',
    it: 'Caricamento non riuscito',
    ru: 'Ошибка загрузки',
  },
  'editor.error.videoUnavailableSubtitle': {
    en: 'Please adjust the sharing link settings',
    es: 'Ajusta la configuración del enlace compartido',
    pt: 'Ajuste as configurações do link de compartilhamento',
    fr: 'Modifiez les paramètres du lien de partage',
    it: 'Modifica le impostazioni di condivisione del link',
    ru: 'Измените настройки доступа по ссылке',
  },
  'editor.error.videoUnavailableTitle': {
    en: 'Video unavailable',
    es: 'Video no disponible',
    pt: 'Vídeo indisponível',
    fr: 'Vidéo indisponible',
    it: 'Video non disponibile',
    ru: 'Видео недоступно',
  },
  'editor.error.workingOnIt': {
    en: 'We are already working to fix this.',
    es: 'Ya estamos trabajando para solucionarlo.',
    pt: 'Já estamos trabalhando para corrigir isso.',
    fr: 'Nous travaillons déjà à la résolution du problème.',
    it: 'Stiamo già lavorando per risolvere il problema.',
    ru: 'Мы уже работаем над исправлением.',
  },
  'editor.feedback.abFlatVoice': {
    en: 'Flat and monotonous voice 😐',
    es: 'Voz plana y monótona 😐',
    pt: 'Voz sem emoção e monótona 😐',
    fr: 'Voix plate et monotone 😐',
    it: 'Voce piatta e monotona 😐',
    ru: 'Невыразительный и монотонный голос 😐',
  },
  'editor.feedback.abLimitedVoices': {
    en: 'Limited voice options 🤏',
    es: 'Pocas opciones de voz 🤏',
    pt: 'Poucas opções de voz 🤏',
    fr: 'Choix de voix limité 🤏',
    it: 'Opzioni vocali limitate 🤏',
    ru: 'Мало вариантов голоса 🤏',
  },
  'editor.feedback.abLowQuality': {
    en: 'Low sound quality 🔈',
    es: 'Baja calidad de sonido 🔈',
    pt: 'Baixa qualidade do som 🔈',
    fr: 'Faible qualité sonore 🔈',
    it: 'Bassa qualità audio 🔈',
    ru: 'Низкое качество звука 🔈',
  },
  'editor.feedback.abNoise': {
    en: 'Noise or audio distortion 💥',
    es: 'Distorsiones por ruido 💥',
    pt: 'Ruído ou distorção de áudio 💥',
    fr: 'Distorsions sonores 💥',
    it: 'Distorsioni da rumore 💥',
    ru: 'Шумы и искажения 💥',
  },
  'editor.feedback.abPronunciation': {
    en: 'Pronunciation errors 🗣️',
    es: 'Errores de pronunciación 🗣️',
    pt: 'Erros de pronúncia 🗣️',
    fr: 'Erreurs de prononciation 🗣️',
    it: 'Errori di pronuncia 🗣️',
    ru: 'Ошибки произношения 🗣️',
  },
  'editor.feedback.abVoiceAccent': {
    en: "Voiceover accent doesn't match the original 🎙️",
    es: 'Acento incorrecto en la voz en off 🎙️',
    pt: 'Sotaque incorreto na narração 🎙️',
    fr: 'Accent de la voix off non conforme à l’original 🎙️',
    it: 'Accento errato nella voce fuori campo 🎙️',
    ru: 'Акцент озвучки не совпадает с оригиналом 🎙️',
  },
  'editor.feedback.abVoiceClone': {
    en: "Voice clone doesn't sound like the original voice 🤷",
    es: 'El clon de voz no suena como la voz original 🤷',
    pt: 'O clone de voz não soa como a voz original 🤷',
    fr: 'Le clone vocal ne ressemble pas à la voix originale 🤷',
    it: 'Il clone vocale non assomiglia alla voce originale 🤷',
    ru: 'Клон голоса не похож на оригинал 🤷',
  },
  'editor.feedback.backgroundNoise': {
    en: 'Background noise 🌫',
    es: 'Ruido de fondo 🌫',
    pt: 'Ruído de fundo 🌫',
    fr: 'Bruit de fond 🌫',
    it: 'Rumore di fondo 🌫',
    ru: 'Фоновый шум 🌫',
  },
  'editor.feedback.commentPlaceholder': {
    en: 'Feedback here',
    es: 'Escribe tus comentarios',
    pt: 'Escreva seu feedback',
    fr: 'Votre commentaire',
    it: 'Scrivi il feedback',
    ru: 'Ваш комментарий',
  },
  'editor.feedback.freeVideoAdded': {
    en: '1 video has been added\nto your account',
    es: 'Se ha añadido 1 video\na tu cuenta',
    pt: '1 vídeo foi adicionado\nà sua conta',
    fr: '1 vidéo a été ajoutée\nà votre compte',
    it: '1 video è stato aggiunto\nal tuo account',
    ru: 'В ваш аккаунт\nдобавлено 1 видео',
  },
  'editor.feedback.originalMismatch': {
    en: 'Original and translated audio mismatch (e.g. speed, pauses) 💬',
    es: 'El audio original y el traducido no coinciden (por ejemplo, velocidad o pausas) 💬',
    pt: 'O áudio original e o traduzido não coincidem (por exemplo, velocidade ou pausas) 💬',
    fr: 'L’audio original et l’audio traduit ne correspondent pas (vitesse, pauses, etc.) 💬',
    it: 'L’audio originale e quello tradotto non corrispondono (ad esempio velocità o pause) 💬',
    ru: 'Оригинальное и переведённое аудио не совпадают (например, скорость или паузы) 💬',
  },
  'editor.feedback.poorTranslation': {
    en: 'Poor text translation 🗺️',
    es: 'Mala traducción del texto 🗺️',
    pt: 'Tradução de texto ruim 🗺️',
    fr: 'Mauvaise traduction du texte 🗺️',
    it: 'Traduzione del testo scadente 🗺️',
    ru: 'Плохой перевод текста 🗺️',
  },
  'editor.feedback.rateFree': {
    en: 'Rate the result and get 1 video for free',
    es: 'Valora el resultado y recibe 1 video gratis',
    pt: 'Avalie o resultado e ganhe 1 vídeo grátis',
    fr: 'Évaluez le résultat et recevez 1 vidéo gratuite',
    it: 'Valuta il risultato e ricevi 1 video gratis',
    ru: 'Оцените результат и получите 1 видео бесплатно',
  },
  'editor.feedback.rateLipSync': {
    en: 'Rate the lip-sync',
    es: 'Valora el Lip-sync',
    pt: 'Avalie o Lip-sync',
    fr: 'Évaluez le Lip-sync',
    it: 'Valuta il lip-sync',
    ru: 'Оцените липсинк',
  },
  'editor.feedback.rateResult': {
    en: 'Rate the result',
    es: 'Valora el resultado',
    pt: 'Avalie o resultado',
    fr: 'Évaluez le résultat',
    it: 'Valuta il risultato',
    ru: 'Оцените результат',
  },
  'editor.feedback.roboticVoice': {
    en: 'Robotic voice 🤖',
    es: 'Voz robótica 🤖',
    pt: 'Voz robótica 🤖',
    fr: 'Voix robotique 🤖',
    it: 'Voce robotica 🤖',
    ru: 'Роботизированный голос 🤖',
  },
  'editor.feedback.speakerIncorrect': {
    en: 'Wrong voice assigned to speaker 👨👩',
    es: 'Voz del hablante asignada incorrectamente 👨👩',
    pt: 'Voz do falante atribuída incorretamente 👨👩',
    fr: 'Voix du locuteur mal attribuée 👨👩',
    it: 'Voce del locutore assegnata in modo errato 👨👩',
    ru: 'Голос спикера назначен неправильно 👨👩',
  },
  'editor.feedback.speechRecognition': {
    en: 'Inaccurate speech-to-text recognition 📝',
    es: 'Reconocimiento de voz impreciso 📝',
    pt: 'Reconhecimento de fala impreciso 📝',
    fr: 'Reconnaissance vocale imprécise 📝',
    it: 'Riconoscimento vocale impreciso 📝',
    ru: 'Неточное распознавание речи 📝',
  },
  'editor.feedback.thankYou': {
    en: 'Thank you',
    es: 'Gracias',
    pt: 'Obrigado',
    fr: 'Merci',
    it: 'Grazie',
    ru: 'Спасибо',
  },
  'editor.feedback.voiceAccent': {
    en: 'Wrong voice accent 🎙',
    es: 'Acento incorrecto en la voz 🎙',
    pt: 'Sotaque incorreto na voz 🎙',
    fr: 'Mauvais accent dans la voix 🎙',
    it: 'Accento errato nella voce 🎙',
    ru: 'Неправильный акцент в голосе 🎙',
  },
  'editor.feedback.voiceClone': {
    en: "Voice clone doesn't sound like the original voice 😞",
    es: 'El clon de voz no suena como la voz original 😞',
    pt: 'O clone de voz não soa como a voz original 😞',
    fr: 'Le clone vocal ne ressemble pas à la voix originale 😞',
    it: 'Il clone vocale non assomiglia alla voce originale 😞',
    ru: 'Клон голоса не похож на оригинал 😞',
  },
  'editor.feedback.whatWentWrong': {
    en: 'What went wrong?',
    es: '¿Qué salió mal?',
    pt: 'O que deu errado?',
    fr: 'Quel est le problème ?',
    it: 'Cosa non ha funzionato?',
    ru: 'Что пошло не так?',
  },
  'editor.fakeSegment.reuploadTitle': {
    en: 'Awesome! Please reupload the video to translate it fully',
    es: '¡Genial! Vuelve a subir el video para traducirlo por completo',
    pt: 'Ótimo! Envie o vídeo novamente para traduzi-lo por completo',
    fr: 'Parfait ! Importez à nouveau la vidéo pour la traduire intégralement',
    it: 'Fantastico! Carica di nuovo il video per tradurlo completamente',
    ru: 'Отлично! Загрузите видео заново, чтобы перевести его целиком',
  },
  'editor.fakeSegment.upgradeSubtitle': {
    en: 'To translate videos over a minute long without a watermark, choose another plan that suits your needs.',
    es: 'Para traducir videos de más de un minuto sin marca de agua, elige otro plan que se adapte a tus necesidades.',
    pt: 'Para traduzir vídeos com mais de um minuto sem marca-d’água, escolha outro plano que atenda às suas necessidades.',
    fr: 'Pour traduire des vidéos de plus d’une minute sans filigrane, choisissez une autre offre adaptée à vos besoins.',
    it: 'Per tradurre video più lunghi di un minuto senza filigrana, scegli un altro piano adatto alle tue esigenze.',
    ru: 'Чтобы переводить видео длиннее минуты без водяного знака, выберите подходящий тариф.',
  },
  'editor.fakeSegment.upgradeTitle': {
    en: 'Upgrade to translate the full video',
    es: 'Mejora tu plan para traducir el video completo',
    pt: 'Faça upgrade para traduzir o vídeo inteiro',
    fr: 'Passez à une offre supérieure pour traduire toute la vidéo',
    it: 'Passa a un piano superiore per tradurre l’intero video',
    ru: 'Перейдите на другой тариф, чтобы перевести видео целиком',
  },
  'editor.lipSyncFeedback.articulationCaptionLeft': {
    en: 'Poor articulation 😔',
    es: 'Mala articulación 😔',
    pt: 'Articulação ruim 😔',
    fr: 'Mauvaise articulation 😔',
    it: 'Articolazione scarsa 😔',
    ru: 'Плохая артикуляция 😔',
  },
  'editor.lipSyncFeedback.articulationCaptionRight': {
    en: 'Great articulation 🤩',
    es: 'Excelente articulación 🤩',
    pt: 'Ótima articulação 🤩',
    fr: 'Excellente articulation 🤩',
    it: 'Ottima articolazione 🤩',
    ru: 'Отличная артикуляция 🤩',
  },
  'editor.lipSyncFeedback.articulationSubtitle': {
    en: 'How natural do the mouth movements look?',
    es: '¿Qué tan naturales se ven los movimientos de la boca?',
    pt: 'Quão naturais parecem os movimentos da boca?',
    fr: 'Les mouvements de la bouche semblent-ils naturels ?',
    it: 'Quanto sembrano naturali i movimenti della bocca?',
    ru: 'Насколько естественно выглядят движения рта?',
  },
  'editor.lipSyncFeedback.articulationTitle': {
    en: 'Please rate: Articulation',
    es: 'Valora la articulación',
    pt: 'Avalie a articulação',
    fr: 'Évaluez l’articulation',
    it: 'Valuta l’articolazione',
    ru: 'Оцените артикуляцию',
  },
  'editor.lipSyncFeedback.consistencyCaptionLeft': {
    en: 'Not consistent at all 😔',
    es: 'Nada consistente 😔',
    pt: 'Nada consistente 😔',
    fr: 'Pas du tout cohérent 😔',
    it: 'Per niente coerente 😔',
    ru: 'Совсем нестабильно 😔',
  },
  'editor.lipSyncFeedback.consistencyCaptionRight': {
    en: 'Very consistent 🤩',
    es: 'Muy consistente 🤩',
    pt: 'Muito consistente 🤩',
    fr: 'Très cohérent 🤩',
    it: 'Molto coerente 🤩',
    ru: 'Очень стабильно 🤩',
  },
  'editor.lipSyncFeedback.consistencySubtitle': {
    en: 'How consistent are the facial features throughout the generated video?',
    es: '¿Qué tan consistentes son los rasgos faciales a lo largo del video generado?',
    pt: 'Quão consistentes são os traços faciais ao longo do vídeo gerado?',
    fr: 'Les traits du visage restent-ils cohérents tout au long de la vidéo générée ?',
    it: 'Quanto sono coerenti i tratti del viso nel video generato?',
    ru: 'Насколько стабильно выглядят черты лица на протяжении сгенерированного видео?',
  },
  'editor.lipSyncFeedback.consistencyTitle': {
    en: 'Please rate: Consistency',
    es: 'Valora la consistencia',
    pt: 'Avalie a consistência',
    fr: 'Évaluez la cohérence',
    it: 'Valuta la coerenza',
    ru: 'Оцените стабильность',
  },
  'editor.lipSyncFeedback.leaveMore': {
    en: 'Leave more feedback here',
    es: 'Deja más comentarios aquí',
    pt: 'Deixe mais comentários aqui',
    fr: 'Ajoutez un commentaire',
    it: 'Lascia altri commenti qui',
    ru: 'Оставьте дополнительный комментарий',
  },
  'editor.lipSyncFeedback.qualityCaptionLeft': {
    en: 'Very blurry, unnatural 😞',
    es: 'Muy borroso y poco natural 😞',
    pt: 'Muito desfocado e artificial 😞',
    fr: 'Très flou et peu naturel 😞',
    it: 'Molto sfocato e innaturale 😞',
    ru: 'Очень размыто и неестественно 😞',
  },
  'editor.lipSyncFeedback.qualityCaptionRight': {
    en: 'Highly detailed, natural 🤩',
    es: 'Muy detallado y natural 🤩',
    pt: 'Muito detalhado e natural 🤩',
    fr: 'Très détaillé et naturel 🤩',
    it: 'Molto dettagliato e naturale 🤩',
    ru: 'Очень детально и естественно 🤩',
  },
  'editor.lipSyncFeedback.qualitySubtitle': {
    en: 'How would you rate the quality of the generated face?',
    es: '¿Cómo valorarías la calidad del rostro generado?',
    pt: 'Como você avalia a qualidade do rosto gerado?',
    fr: 'Comment évaluez-vous la qualité du visage généré ?',
    it: 'Come valuti la qualità del volto generato?',
    ru: 'Как вы оцениваете качество созданного лица?',
  },
  'editor.lipSyncFeedback.qualityTitle': {
    en: 'Please rate: Picture quality',
    es: 'Valora la calidad de imagen',
    pt: 'Avalie a qualidade da imagem',
    fr: 'Évaluez la qualité de l’image',
    it: 'Valuta la qualità dell’immagine',
    ru: 'Оцените качество изображения',
  },
  'editor.lipSyncFeedback.similarityCaptionLeft': {
    en: 'Completely different 😔',
    es: 'Completamente diferente 😔',
    pt: 'Completamente diferente 😔',
    fr: 'Complètement différent 😔',
    it: 'Completamente diverso 😔',
    ru: 'Совсем не похож 😔',
  },
  'editor.lipSyncFeedback.similarityCaptionRight': {
    en: 'Looks like the same person to me 🤩',
    es: 'Parece la misma persona 🤩',
    pt: 'Parece a mesma pessoa 🤩',
    fr: 'On dirait la même personne 🤩',
    it: 'Sembra la stessa persona 🤩',
    ru: 'Похож на того же человека 🤩',
  },
  'editor.lipSyncFeedback.similaritySubtitle': {
    en: 'How closely does the result resemble the original face, lips and teeth?',
    es: '¿Cuánto se parece el resultado al rostro, los labios y los dientes originales?',
    pt: 'Quanto o resultado se parece com o rosto, os lábios e os dentes originais?',
    fr: 'Dans quelle mesure le résultat ressemble-t-il au visage, aux lèvres et aux dents d’origine ?',
    it: 'Quanto assomiglia il risultato al volto, alle labbra e ai denti originali?',
    ru: 'Насколько результат похож на исходное лицо, включая губы и зубы?',
  },
  'editor.lipSyncFeedback.similarityTitle': {
    en: 'Please rate: Similarity',
    es: 'Valora el parecido',
    pt: 'Avalie a semelhança',
    fr: 'Évaluez la ressemblance',
    it: 'Valuta la somiglianza',
    ru: 'Оцените сходство',
  },
  'editor.lipSyncFeedback.syncCaptionLeft': {
    en: 'Poor synchronization 😔',
    es: 'Mala sincronización 😔',
    pt: 'Sincronização ruim 😔',
    fr: 'Mauvaise synchronisation 😔',
    it: 'Sincronizzazione scarsa 😔',
    ru: 'Плохая синхронизация 😔',
  },
  'editor.lipSyncFeedback.syncCaptionRight': {
    en: 'Great synchronization 🤩',
    es: 'Excelente sincronización 🤩',
    pt: 'Ótima sincronização 🤩',
    fr: 'Excellente synchronisation 🤩',
    it: 'Ottima sincronizzazione 🤩',
    ru: 'Отличная синхронизация 🤩',
  },
  'editor.lipSyncFeedback.syncSubtitle': {
    en: 'How accurately does the mouth movement match the audio?',
    es: '¿Con qué precisión coinciden los movimientos de la boca con el audio?',
    pt: 'Com que precisão o movimento da boca corresponde ao áudio?',
    fr: 'Les mouvements de la bouche correspondent-ils précisément à l’audio ?',
    it: 'Con quale precisione i movimenti della bocca corrispondono all’audio?',
    ru: 'Насколько точно движения рта совпадают с аудио?',
  },
  'editor.lipSyncFeedback.syncTitle': {
    en: 'Please rate: Synchronization',
    es: 'Valora la sincronización',
    pt: 'Avalie a sincronização',
    fr: 'Évaluez la synchronisation',
    it: 'Valuta la sincronizzazione',
    ru: 'Оцените синхронизацию',
  },
  'editor.lipSync.aboutUpdate': {
    en: 'about the lip-sync update',
    es: 'sobre la actualización del Lip-sync',
    pt: 'sobre a atualização do Lip-sync',
    fr: 'sur la mise à jour du Lip-sync',
    it: 'sull’aggiornamento del lip-sync',
    ru: 'об обновлении липсинка',
  },
  'editor.lipSync.betaPricing': {
    en: 'Beta pricing',
    es: 'Precio beta',
    pt: 'Preço beta',
    fr: 'Tarification bêta',
    it: 'Prezzi in beta',
    ru: 'Бета-цены',
  },
  'editor.lipSync.betaPricingHint': {
    en: 'Enhanced pricing is in beta and may change.',
    es: 'El precio de Enhanced está en fase beta y puede cambiar.',
    pt: 'O preço do Enhanced está em fase beta e pode mudar.',
    fr: 'La tarification Enhanced est en version bêta et peut évoluer.',
    it: 'I prezzi del modello Enhanced sono in beta e potrebbero cambiare.',
    ru: 'Тарифы Enhanced находятся в бета-версии и могут измениться.',
  },
  'editor.lipSync.criteriaFace': {
    en: 'The face is clearly visible',
    es: 'El rostro se ve claramente',
    pt: 'O rosto está claramente visível',
    fr: 'Le visage est clairement visible',
    it: 'Il volto è chiaramente visibile',
    ru: 'Лицо хорошо видно',
  },
  'editor.lipSync.criteriaLight': {
    en: 'The lighting is good, with no shadows or overexposure',
    es: 'La iluminación es buena, sin sombras ni sobreexposición',
    pt: 'A iluminação está boa, sem sombras ou superexposição',
    fr: 'L’éclairage est bon, sans ombres ni surexposition',
    it: 'La luce è buona, senza ombre o sovraesposizione',
    ru: 'Хорошее освещение, без теней и пересвета',
  },
  'editor.lipSync.criteriaNotCovered': {
    en: 'Nothing covers the eyes, nose, mouth, and eyebrows',
    es: 'Nada cubre los ojos, la nariz, la boca ni las cejas',
    pt: 'Nada cobre os olhos, nariz, boca e sobrancelhas',
    fr: 'Rien ne couvre les yeux, le nez, la bouche ni les sourcils',
    it: 'Nulla copre occhi, naso, bocca e sopracciglia',
    ru: 'Глаза, нос, рот и брови ничем не закрыты',
  },
  'editor.lipSync.criteriaTitle': {
    en: 'Before we start, make sure your video meets these criteria:',
    es: 'Antes de empezar, comprueba que el video cumple estos requisitos:',
    pt: 'Antes de começar, verifique se o vídeo atende a estes critérios:',
    fr: 'Avant de commencer, vérifiez que votre vidéo respecte ces critères :',
    it: 'Prima di iniziare, assicurati che il video soddisfi questi criteri:',
    ru: 'Перед началом убедитесь, что видео соответствует этим требованиям:',
  },
  'editor.lipSync.criteriaPassed': {
    en: 'Your video met all criteria ✓',
    es: 'Tu video cumple todos los requisitos ✓',
    pt: 'Seu vídeo atendeu a todos os critérios ✓',
    fr: 'Votre vidéo respecte tous les critères ✓',
    it: 'Il video soddisfa tutti i criteri ✓',
    ru: 'Видео соответствует всем требованиям ✓',
  },
  'editor.lipSync.detectFaceFailed': {
    en: 'Lip-sync failed: we could not detect a face in this video.',
    es: 'Error de Lip-sync: no pudimos detectar un rostro en este video.',
    pt: 'Falha no Lip-sync: não foi possível detectar um rosto neste vídeo.',
    fr: 'Échec du Lip-sync : aucun visage détecté dans cette vidéo.',
    it: 'Lip-sync non riuscito: non è stato rilevato alcun volto nel video.',
    ru: 'Не удалось создать липсинк: в видео не найдено лицо.',
  },
  'editor.lipSync.check': {
    en: 'check',
    es: '— Comprobar',
    pt: '— Verificar',
    fr: 'à vérifier',
    it: '(da verificare)',
    ru: '— проверить',
  },
  'editor.lipSync.enhanced': {
    en: 'Enhanced (new)',
    es: 'Enhanced (nuevo)',
    pt: 'Enhanced (novo)',
    fr: 'Enhanced (nouveau)',
    it: 'Enhanced (nuovo)',
    ru: 'Enhanced (новая)',
  },
  'editor.lipSync.enhancedIntro': {
    en: 'Enhanced lip-sync is here',
    es: 'Ya está disponible el Lip-sync Enhanced',
    pt: 'O Lip-sync Enhanced chegou',
    fr: 'Le Lip-sync Enhanced est disponible',
    it: 'È arrivato il lip-sync Enhanced',
    ru: 'Доступен липсинк Enhanced',
  },
  'editor.lipSync.enhancedSubtitleFew': {
    en: 'Our new Enhanced lip-sync model delivers studio-grade quality. We deduct {multiplier} minutes from your balance for each minute of video. Re-runs cost the same.',
    es: 'Nuestro nuevo modelo Enhanced ofrece calidad de estudio. Se descuentan {multiplier} minutos del saldo por cada minuto de video. Las repeticiones se cobran igual.',
    pt: 'Nosso novo modelo Enhanced oferece qualidade de estúdio. {multiplier} minutos do saldo são descontados por cada minuto de vídeo. Novas execuções têm o mesmo custo.',
    fr: 'Notre nouveau modèle Enhanced offre une qualité studio. {multiplier} minutes sont déduites du solde par minute de vidéo. Les nouvelles générations sont facturées au même tarif.',
    it: 'Il nuovo modello Enhanced offre qualità da studio. Per ogni minuto di video vengono detratti {multiplier} minuti dal saldo. Le ripetizioni hanno lo stesso costo.',
    ru: 'Новая модель Enhanced обеспечивает студийное качество. За 1 минуту видео списывается {multiplier} минуты баланса. При повторном запуске списывается столько же минут.',
  },
  'editor.lipSync.enhancedSubtitleMany': {
    en: 'Our new Enhanced lip-sync model delivers studio-grade quality. We deduct {multiplier} minutes from your balance for each minute of video. Re-runs cost the same.',
    es: 'Nuestro nuevo modelo Enhanced ofrece calidad de estudio. Se descuentan {multiplier} minutos del saldo por cada minuto de video. Las repeticiones se cobran igual.',
    pt: 'Nosso novo modelo Enhanced oferece qualidade de estúdio. {multiplier} minutos do saldo são descontados por cada minuto de vídeo. Novas execuções têm o mesmo custo.',
    fr: 'Notre nouveau modèle Enhanced offre une qualité studio. {multiplier} minutes sont déduites du solde par minute de vidéo. Les nouvelles générations sont facturées au même tarif.',
    it: 'Il nuovo modello Enhanced offre qualità da studio. Per ogni minuto di video vengono detratti {multiplier} minuti dal saldo. Le ripetizioni hanno lo stesso costo.',
    ru: 'Новая модель Enhanced обеспечивает студийное качество. За 1 минуту видео списывается {multiplier} минут баланса. При повторном запуске списывается столько же минут.',
  },
  'editor.lipSync.enhancedSubtitleOne': {
    en: 'Our new Enhanced lip-sync model delivers studio-grade quality. We deduct {multiplier} minute from your balance for each minute of video. Re-runs cost the same.',
    es: 'Nuestro nuevo modelo Enhanced ofrece calidad de estudio. Se descuenta {multiplier} minuto del saldo por cada minuto de video. Las repeticiones se cobran igual.',
    pt: 'Nosso novo modelo Enhanced oferece qualidade de estúdio. {multiplier} minuto do saldo é descontado por cada minuto de vídeo. Novas execuções têm o mesmo custo.',
    fr: 'Notre nouveau modèle Enhanced offre une qualité studio. {multiplier} minute est déduite du solde par minute de vidéo. Les nouvelles générations sont facturées au même tarif.',
    it: 'Il nuovo modello Enhanced offre qualità da studio. Per ogni minuto di video viene detratto {multiplier} minuto dal saldo. Le ripetizioni hanno lo stesso costo.',
    ru: 'Новая модель Enhanced обеспечивает студийное качество. За 1 минуту видео списывается {multiplier} минута баланса. При повторном запуске списывается столько же минут.',
  },
  'editor.lipSync.enhancedSubtitleOther': {
    en: 'Our new Enhanced lip-sync model delivers studio-grade quality. We deduct {multiplier} minutes from your balance for each minute of video. Re-runs cost the same.',
    es: 'Nuestro nuevo modelo Enhanced ofrece calidad de estudio. Se descuentan {multiplier} minutos del saldo por cada minuto de video. Las repeticiones se cobran igual.',
    pt: 'Nosso novo modelo Enhanced oferece qualidade de estúdio. {multiplier} minutos do saldo são descontados por cada minuto de vídeo. Novas execuções têm o mesmo custo.',
    fr: 'Notre nouveau modèle Enhanced offre une qualité studio. {multiplier} minutes sont déduites du solde par minute de vidéo. Les nouvelles générations sont facturées au même tarif.',
    it: 'Il nuovo modello Enhanced offre qualità da studio. Per ogni minuto di video vengono detratti {multiplier} minuti dal saldo. Le ripetizioni hanno lo stesso costo.',
    ru: 'Новая модель Enhanced обеспечивает студийное качество. За 1 минуту видео списывается {multiplier} минуты баланса. При повторном запуске списывается столько же минут.',
  },
  'editor.lipSync.faceRetry': {
    en: 'Make sure the face is clearly visible and not covered, then try again.',
    es: 'Comprueba que el rostro se ve claramente y no está cubierto, y vuelve a intentarlo.',
    pt: 'Verifique se o rosto está claramente visível e não está coberto e tente novamente.',
    fr: 'Vérifiez que le visage est clairement visible et dégagé, puis réessayez.',
    it: 'Assicurati che il volto sia chiaramente visibile e non coperto, poi riprova.',
    ru: 'Убедитесь, что лицо хорошо видно и ничем не закрыто, затем попробуйте снова.',
  },
  'editor.lipSync.free': { en: 'Free', es: 'Gratis', pt: 'Grátis', fr: 'Gratuit', it: 'Gratis', ru: 'Бесплатно' },
  'editor.lipSync.freeFull': {
    en: 'Lip-syncing the video again is free',
    es: 'Volver a hacer el Lip-sync del video es gratis',
    pt: 'Refazer o Lip-sync do vídeo é grátis',
    fr: 'Relancer le Lip-sync de la vidéo est gratuit',
    it: 'Ripetere il lip-sync del video è gratuito',
    ru: 'Повторный липсинк видео — бесплатно',
  },
  'editor.lipSync.freeMinuteButton': {
    en: 'Lip-sync 1 min',
    es: 'Lip-sync de 1 min',
    pt: 'Lip-sync de 1 min',
    fr: 'Lip-sync de 1 min',
    it: 'Lip-sync di 1 min',
    ru: 'Липсинк 1 мин',
  },
  'editor.lipSync.freeMinuteSubtitle': {
    en: "We'll lip-sync 1 minute of your video for you to rate the result.",
    es: 'Haremos Lip-sync de 1 minuto de tu video para que puedas valorar el resultado.',
    pt: 'Faremos o Lip-sync de 1 minuto do seu vídeo para você avaliar o resultado.',
    fr: 'Nous appliquerons le Lip-sync à 1 minute de votre vidéo pour vous permettre d’évaluer le résultat.',
    it: 'Eseguiremo il lip-sync di 1 minuto del video per permetterti di valutare il risultato.',
    ru: 'Мы сделаем липсинк для 1 минуты вашего видео, чтобы вы могли оценить результат.',
  },
  'editor.lipSync.freeMinuteTitle': {
    en: "We'll lip-sync 1 minute of this video for free",
    es: 'Haremos gratis el Lip-sync de un minuto de este video',
    pt: 'Faremos gratuitamente o Lip-sync de 1 minuto deste vídeo',
    fr: 'Nous appliquerons gratuitement le Lip-sync à 1 minute de cette vidéo',
    it: 'Eseguiremo gratis il lip-sync di 1 minuto di questo video',
    ru: 'Мы бесплатно сделаем липсинк для минутного фрагмента этого видео',
  },
  'editor.lipSync.fullVideo': {
    en: 'Lip-sync full video',
    es: 'Aplicar Lip-sync al video completo',
    pt: 'Aplicar Lip-sync ao vídeo completo',
    fr: 'Appliquer le Lip-sync à toute la vidéo',
    it: 'Applica il lip-sync all’intero video',
    ru: 'Липсинк всего видео',
  },
  'editor.lipSync.fullCostFew': {
    en: "We'll lip-sync this video and deduct {minutes} minutes from your account",
    es: 'Haremos el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous appliquerons le Lip-sync à cette vidéo et déduirons {minutes} minutes de votre compte',
    it: 'Eseguiremo il lip-sync del video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы сделаем липсинк этого видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.lipSync.fullCostMany': {
    en: "We'll lip-sync this video and deduct {minutes} minutes from your account",
    es: 'Haremos el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous appliquerons le Lip-sync à cette vidéo et déduirons {minutes} minutes de votre compte',
    it: 'Eseguiremo il lip-sync del video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы сделаем липсинк этого видео и спишем {minutes} минут с вашего аккаунта',
  },
  'editor.lipSync.fullCostOne': {
    en: "We'll lip-sync this video and deduct {minutes} minute from your account",
    es: 'Haremos el Lip-sync de este video y descontaremos {minutes} minuto de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo e descontaremos {minutes} minuto da sua conta',
    fr: 'Nous appliquerons le Lip-sync à cette vidéo et déduirons {minutes} minute de votre compte',
    it: 'Eseguiremo il lip-sync del video e detrarremo {minutes} minuto dal tuo account',
    ru: 'Мы сделаем липсинк этого видео и спишем {minutes} минуту с вашего аккаунта',
  },
  'editor.lipSync.fullCostOther': {
    en: "We'll lip-sync this video and deduct {minutes} minutes from your account",
    es: 'Haremos el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous appliquerons le Lip-sync à cette vidéo et déduirons {minutes} minutes de votre compte',
    it: 'Eseguiremo il lip-sync del video e detrarremo {minutes} minuti dal tuo account',
    ru: 'Мы сделаем липсинк этого видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.lipSync.fullCostPrefix': {
    en: "We'll lip-sync this video and deduct",
    es: 'Haremos el Lip-sync de este video y descontaremos',
    pt: 'Faremos o Lip-sync deste vídeo e descontaremos',
    fr: 'Nous effectuerons le Lip-sync de cette vidéo et déduirons',
    it: 'Eseguiremo il lip-sync del video e detrarremo',
    ru: 'Мы сделаем липсинк этого видео и спишем',
  },
  'editor.lipSync.fullCostSuffix': {
    en: 'from your account',
    es: 'de tu cuenta',
    pt: 'da sua conta',
    fr: 'de votre compte',
    it: 'dal tuo account',
    ru: 'с вашего аккаунта',
  },
  'editor.lipSync.helpUse': {
    en: 'about how to use lip-sync effectively',
    es: 'para usar el Lip-sync de forma eficaz',
    pt: 'para usar o Lip-sync com eficiência',
    fr: 'pour utiliser efficacement le Lip-sync',
    it: 'per usare il lip-sync in modo efficace',
    ru: 'о том, как эффективно использовать липсинк',
  },
  'editor.lipSync.inProgress': {
    en: 'Lip-sync in progress',
    es: 'Lip-sync en curso',
    pt: 'Lip-sync em andamento',
    fr: 'Lip-sync en cours',
    it: 'Lip-sync in corso',
    ru: 'Создаём липсинк',
  },
  'editor.lipSync.inProgressSubtitle': {
    en: "This usually takes a few minutes. You can leave this page — we'll notify you when it's ready.",
    es: 'Suele tardar unos minutos. Puedes salir de esta página; te avisaremos cuando esté listo.',
    pt: 'Isso geralmente leva alguns minutos. Você pode sair desta página — avisaremos quando estiver pronto.',
    fr: 'Cela prend généralement quelques minutes. Vous pouvez quitter cette page : nous vous préviendrons lorsque ce sera prêt.',
    it: 'Di solito servono alcuni minuti. Puoi lasciare questa pagina: ti avviseremo quando sarà pronto.',
    ru: 'Обычно это занимает несколько минут. Можно уйти со страницы — мы сообщим, когда всё будет готово.',
  },
  'editor.lipSync.legacy': { en: 'Legacy', es: 'Legacy', pt: 'Legacy', fr: 'Legacy', it: 'Legacy', ru: 'Legacy' },
  'editor.lipSync.minuteFew': {
    en: '{minutes} minutes',
    es: '{minutes} minutos',
    pt: '{minutes} minutos',
    fr: '{minutes} minutes',
    it: '{minutes} minuti',
    ru: '{minutes} минуты',
  },
  'editor.lipSync.minuteMany': {
    en: '{minutes} minutes',
    es: '{minutes} minutos',
    pt: '{minutes} minutos',
    fr: '{minutes} minutes',
    it: '{minutes} minuti',
    ru: '{minutes} минут',
  },
  'editor.lipSync.minuteOne': {
    en: '{minutes} minute',
    es: '{minutes} minuto',
    pt: '{minutes} minuto',
    fr: '{minutes} minute',
    it: '{minutes} minuto',
    ru: '{minutes} минуту',
  },
  'editor.lipSync.minuteOther': {
    en: '{minutes} minutes',
    es: '{minutes} minutos',
    pt: '{minutes} minutos',
    fr: '{minutes} minutes',
    it: '{minutes} minuti',
    ru: '{minutes} минуты',
  },
  'editor.lipSync.minuteCost': {
    en: 'One minute of lip-synced video costs one minute from your balance.',
    es: 'Un minuto de video con Lip-sync cuesta un minuto de tu saldo.',
    pt: 'Um minuto de vídeo com Lip-sync custa um minuto do seu saldo.',
    fr: 'Une minute de vidéo avec le Lip-sync coûte une minute de votre solde.',
    it: 'Un minuto di video con lip-sync costa un minuto del saldo.',
    ru: 'Одна минута видео с липсинком стоит одну минуту баланса.',
  },
  'editor.lipSync.model': {
    en: 'Lip-sync model',
    es: 'Modelo de Lip-sync',
    pt: 'Modelo de Lip-sync',
    fr: 'Modèle de Lip-sync',
    it: 'Modello lip-sync',
    ru: 'Модель липсинка',
  },
  'editor.lipSync.new': { en: 'New!', es: '¡Nuevo!', pt: 'Novo!', fr: 'Nouvelle !', it: 'Novità!', ru: 'Новинка!' },
  'editor.lipSync.newUserTitle': {
    en: 'Match mouth movements to translated audio with lip-sync',
    es: 'Sincroniza los movimientos de la boca con el audio traducido mediante Lip-sync',
    pt: 'Sincronize os movimentos da boca com o áudio traduzido usando Lip-sync',
    fr: 'Faites correspondre les mouvements de la bouche à l’audio traduit grâce au Lip-sync',
    it: 'Sincronizza i movimenti della bocca con l’audio tradotto tramite il lip-sync',
    ru: 'Синхронизируйте движения губ с переведённым аудио с помощью липсинка',
  },
  'editor.lipSync.newVersionSubtitle': {
    en: "We updated our lip-sync quality. Now, it's charged the same as translation: 1 minute of lip-sync costs 1 minute.",
    es: 'Hemos mejorado la calidad del Lip-sync. Ahora se cobra igual que la traducción: 1 minuto de Lip-sync cuesta 1 minuto.',
    pt: 'Atualizamos a qualidade do Lip-sync. Agora ele custa o mesmo que a tradução: 1 minuto de Lip-sync custa 1 minuto.',
    fr: 'Nous avons amélioré la qualité du Lip-sync. Il est désormais facturé comme la traduction : 1 minute de Lip-sync coûte 1 minute.',
    it: 'Abbiamo migliorato la qualità del lip-sync. Ora costa come la traduzione: 1 minuto di lip-sync costa 1 minuto.',
    ru: 'Мы улучшили качество липсинка. Теперь он тарифицируется как перевод: 1 минута липсинка стоит 1 минуту.',
  },
  'editor.lipSync.newVersionTitle': {
    en: 'New version of Lip-sync is here!',
    es: '¡Ya está disponible la nueva versión de Lip-sync!',
    pt: 'A nova versão do Lip-sync chegou!',
    fr: 'La nouvelle version du Lip-sync est disponible !',
    it: 'È arrivata la nuova versione del lip-sync!',
    ru: 'Доступна новая версия липсинка!',
  },
  'editor.lipSync.processing': {
    en: 'Processing your video…',
    es: 'Procesando tu video…',
    pt: 'Processando seu vídeo…',
    fr: 'Traitement de votre vidéo…',
    it: 'Elaborazione del video…',
    ru: 'Обрабатываем видео…',
  },
  'editor.lipSync.processingError': {
    en: 'Something went wrong during processing',
    es: 'Algo salió mal durante el procesamiento',
    pt: 'Algo deu errado durante o processamento',
    fr: 'Une erreur est survenue pendant le traitement',
    it: 'Si è verificato un errore durante l’elaborazione',
    ru: 'Во время обработки произошла ошибка',
  },
  'editor.lipSync.queue': {
    en: 'Queue: {number}',
    es: 'Cola: {number}',
    pt: 'Fila: {number}',
    fr: 'File d’attente : {number}',
    it: 'Coda: {number}',
    ru: 'Очередь: {number}',
  },
  'editor.lipSync.queueBeforeFew': {
    en: 'Your video is in the queue. There are {count} projects processing before yours.',
    es: 'Tu video está en la cola. Hay {count} proyectos procesándose antes que el tuyo.',
    pt: 'Seu vídeo está na fila. Há {count} projetos em processamento antes dele.',
    fr: 'Votre vidéo est dans la file d’attente. {count} projets sont traités avant le vôtre.',
    it: 'Il video è in coda. Ci sono {count} progetti in elaborazione prima del tuo.',
    ru: 'Видео в очереди. Перед ним обрабатываются {count} проекта.',
  },
  'editor.lipSync.queueBeforeMany': {
    en: 'Your video is in the queue. There are {count} projects processing before yours.',
    es: 'Tu video está en la cola. Hay {count} proyectos procesándose antes que el tuyo.',
    pt: 'Seu vídeo está na fila. Há {count} projetos em processamento antes dele.',
    fr: 'Votre vidéo est dans la file d’attente. {count} projets sont traités avant le vôtre.',
    it: 'Il video è in coda. Ci sono {count} progetti in elaborazione prima del tuo.',
    ru: 'Видео в очереди. Перед ним обрабатываются {count} проектов.',
  },
  'editor.lipSync.queueBeforeOne': {
    en: 'Your video is in the queue. There is {count} project processing before yours.',
    es: 'Tu video está en la cola. Hay {count} proyecto procesándose antes que el tuyo.',
    pt: 'Seu vídeo está na fila. Há {count} projeto em processamento antes dele.',
    fr: 'Votre vidéo est dans la file d’attente. {count} projet est traité avant le vôtre.',
    it: 'Il video è in coda. C’è {count} progetto in elaborazione prima del tuo.',
    ru: 'Видео в очереди. Перед ним обрабатывается {count} проект.',
  },
  'editor.lipSync.queueBeforeOther': {
    en: 'Your video is in the queue. There are {count} projects processing before yours.',
    es: 'Tu video está en la cola. Hay {count} proyectos procesándose antes que el tuyo.',
    pt: 'Seu vídeo está na fila. Há {count} projetos em processamento antes dele.',
    fr: 'Votre vidéo est dans la file d’attente. {count} projets sont traités avant le vôtre.',
    it: 'Il video è in coda. Ci sono {count} progetti in elaborazione prima del tuo.',
    ru: 'Видео в очереди. Перед ним обрабатываются {count} проекта.',
  },
  'editor.lipSync.reCostFew': {
    en: "We'll lip-sync this video again and deduct {minutes} minutes from your account",
    es: 'Volveremos a hacer el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo novamente e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous effectuerons à nouveau le Lip-sync de cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Eseguiremo nuovamente il lip-sync del video e detrarremo {minutes} minuti dal tuo saldo',
    ru: 'Мы снова сделаем липсинк этого видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.lipSync.reCostMany': {
    en: "We'll lip-sync this video again and deduct {minutes} minutes from your account",
    es: 'Volveremos a hacer el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo novamente e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous effectuerons à nouveau le Lip-sync de cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Eseguiremo nuovamente il lip-sync del video e detrarremo {minutes} minuti dal tuo saldo',
    ru: 'Мы снова сделаем липсинк этого видео и спишем {minutes} минут с вашего аккаунта',
  },
  'editor.lipSync.reCostOne': {
    en: "We'll lip-sync this video again and deduct {minutes} minute from your account",
    es: 'Volveremos a hacer el Lip-sync de este video y descontaremos {minutes} minuto de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo novamente e descontaremos {minutes} minuto da sua conta',
    fr: 'Nous effectuerons à nouveau le Lip-sync de cette vidéo et déduirons {minutes} minute de votre solde',
    it: 'Eseguiremo nuovamente il lip-sync del video e detrarremo {minutes} minuto dal tuo saldo',
    ru: 'Мы снова сделаем липсинк этого видео и спишем {minutes} минуту с вашего аккаунта',
  },
  'editor.lipSync.reCostOther': {
    en: "We'll lip-sync this video again and deduct {minutes} minutes from your account",
    es: 'Volveremos a hacer el Lip-sync de este video y descontaremos {minutes} minutos de tu cuenta',
    pt: 'Faremos o Lip-sync deste vídeo novamente e descontaremos {minutes} minutos da sua conta',
    fr: 'Nous effectuerons à nouveau le Lip-sync de cette vidéo et déduirons {minutes} minutes de votre solde',
    it: 'Eseguiremo nuovamente il lip-sync del video e detrarremo {minutes} minuti dal tuo saldo',
    ru: 'Мы снова сделаем липсинк этого видео и спишем {minutes} минуты с вашего аккаунта',
  },
  'editor.lipSync.regenerationCost': {
    en: 'Regeneration is charged the same as the first run.',
    es: 'Volver a generarlo cuesta lo mismo que la primera vez.',
    pt: 'A nova geração tem o mesmo custo da primeira.',
    fr: 'La régénération est facturée au même tarif que la première exécution.',
    it: 'La ripetizione ha lo stesso costo della prima esecuzione.',
    ru: 'Повторный запуск оплачивается так же, как и первый.',
  },
  'editor.lipSync.retryGeneric': {
    en: 'Please try again. If the problem persists, contact support.',
    es: 'Inténtalo de nuevo. Si el problema continúa, contacta con soporte.',
    pt: 'Tente novamente. Se o problema persistir, entre em contato com o suporte.',
    fr: 'Réessayez. Si le problème persiste, contactez l’assistance.',
    it: 'Riprova. Se il problema persiste, contatta l’assistenza.',
    ru: 'Попробуйте снова. Если проблема повторится, обратитесь в поддержку.',
  },
  'editor.lipSync.start': {
    en: 'Start Lip-sync',
    es: 'Iniciar Lip-sync',
    pt: 'Iniciar Lip-sync',
    fr: 'Lancer le Lip-sync',
    it: 'Avvia lip-sync',
    ru: 'Запустить липсинк',
  },
  'editor.lipSync.starting': {
    en: 'Starting Lip-sync...',
    es: 'Iniciando Lip-sync...',
    pt: 'Iniciando Lip-sync...',
    fr: 'Lancement du Lip-sync…',
    it: 'Avvio lip-sync...',
    ru: 'Запускаем липсинк…',
  },
  'editor.lipSync.tryFree': {
    en: 'Try Lip-sync for free',
    es: 'Prueba Lip-sync gratis',
    pt: 'Experimente Lip-sync grátis',
    fr: 'Essayez gratuitement le Lip-sync',
    it: 'Prova gratis il lip-sync',
    ru: 'Попробуйте липсинк бесплатно',
  },
  'editor.lipSync.upgradeFull': {
    en: 'Upgrade to lip-sync the full video',
    es: 'Mejora tu plan para aplicar Lip-sync al video completo',
    pt: 'Fazer upgrade para aplicar Lip-sync ao vídeo completo',
    fr: 'Passez à une offre supérieure pour appliquer le Lip-sync à toute la vidéo',
    it: 'Passa a un piano superiore per applicare il lip-sync all’intero video',
    ru: 'Перейти на другой тариф для липсинка всего видео',
  },
  'editor.lipSync.fullOfferSubtitle': {
    en: 'We only lip-synced 1 minute of your video for you to rate the result',
    es: 'Solo aplicamos Lip-sync a 1 minuto del video para que puedas valorar el resultado',
    pt: 'Aplicamos Lip-sync em apenas 1 minuto do seu vídeo para você avaliar o resultado',
    fr: 'Nous avons appliqué le Lip-sync à seulement 1 minute de votre vidéo pour vous permettre d’évaluer le résultat',
    it: 'Abbiamo applicato il lip-sync a solo 1 minuto del video per permetterti di valutare il risultato',
    ru: 'Мы сделали липсинк только для минутного фрагмента вашего видео, чтобы вы могли оценить результат',
  },
  'editor.lipSync.timeCost': {
    en: 'Time cost: {minutes} min',
    es: 'Coste: {minutes} min',
    pt: 'Custo: {minutes} min',
    fr: 'Coût : {minutes} min',
    it: 'Costo: {minutes} min',
    ru: 'Стоимость: {minutes} мин',
  },
  'editor.lipSync.upgradeOfferSubtitle': {
    en: 'To generate lip-sync for the full video, upgrade to the Creator Pro plan',
    es: 'Para generar un video completo con Lip-sync, mejora tu plan a Creator Pro',
    pt: 'Para gerar um vídeo completo com Lip-sync, faça upgrade para o plano Creator Pro',
    fr: 'Pour générer une vidéo entière avec le Lip-sync, passez à l’offre Creator Pro',
    it: 'Per generare il lip-sync per l’intero video, passa al piano Creator Pro',
    ru: 'Чтобы создать липсинк для всего видео, перейдите на тариф Creator Pro',
  },
  'editor.lipSync.faceNotDetected': {
    en: "We couldn't detect a face in this video",
    es: 'No pudimos detectar un rostro en este video',
    pt: 'Não foi possível detectar um rosto neste vídeo',
    fr: 'Aucun visage détecté dans cette vidéo',
    it: 'Non è stato rilevato alcun volto nel video',
    ru: 'Не удалось найти лицо в видео',
  },
  'editor.glossary.actionsAfterProcessing': {
    en: 'Dictionary actions will be available after processing',
    es: 'Las acciones del diccionario estarán disponibles después del procesamiento',
    pt: 'As ações do dicionário estarão disponíveis após o processamento',
    fr: 'Les actions du dictionnaire seront disponibles après le traitement',
    it: 'Le azioni del dizionario saranno disponibili al termine dell’elaborazione',
    ru: 'Действия со словарём станут доступны после обработки',
  },
  'editor.glossary.addHint': {
    en: 'Your custom translations will be displayed here. You can apply them to all future projects.',
    es: 'Tus traducciones personalizadas aparecerán aquí. Podrás aplicarlas a todos los proyectos futuros.',
    pt: 'Suas traduções personalizadas aparecerão aqui. Você poderá aplicá-las a todos os projetos futuros.',
    fr: 'Vos traductions personnalisées apparaîtront ici. Vous pourrez les appliquer à tous vos futurs projets.',
    it: 'Le traduzioni personalizzate verranno visualizzate qui. Potrai applicarle a tutti i progetti futuri.',
    ru: 'Здесь появятся ваши варианты перевода. Их можно будет использовать во всех будущих проектах.',
  },
  'editor.glossary.alreadyExists': {
    en: 'The word is already in your glossary — edit the existing translation.',
    es: 'La palabra ya está en tu diccionario: edita la traducción existente.',
    pt: 'A palavra já está no seu dicionário — edite a tradução existente.',
    fr: 'Ce mot figure déjà dans votre dictionnaire : modifiez la traduction existante.',
    it: 'La parola è già presente nel dizionario: modifica la traduzione esistente.',
    ru: 'Это слово уже есть в словаре — измените существующий перевод.',
  },
  'editor.glossary.chooseLanguage': {
    en: 'Choose language',
    es: 'Elegir idioma',
    pt: 'Escolher idioma',
    fr: 'Choisir une langue',
    it: 'Scegli lingua',
    ru: 'Выберите язык',
  },
  'editor.glossary.create': {
    en: 'Create dictionary',
    es: 'Crear diccionario',
    pt: 'Criar dicionário',
    fr: 'Créer un dictionnaire',
    it: 'Crea dizionario',
    ru: 'Создать словарь',
  },
  'editor.glossary.createError': {
    en: 'Error creating glossary',
    es: 'Error al crear el diccionario',
    pt: 'Erro ao criar o dicionário',
    fr: 'Erreur lors de la création du dictionnaire',
    it: 'Errore durante la creazione del dizionario',
    ru: 'Не удалось создать словарь',
  },
  'editor.glossary.createNew': {
    en: 'Create new',
    es: 'Crear nuevo',
    pt: 'Criar novo',
    fr: 'Créer',
    it: 'Crea nuovo',
    ru: 'Создать новый',
  },
  'editor.glossary.createNewTitle': {
    en: 'Create new glossary',
    es: 'Crear un diccionario',
    pt: 'Criar novo dicionário',
    fr: 'Créer un nouveau dictionnaire',
    it: 'Crea un nuovo dizionario',
    ru: 'Создать новый словарь',
  },
  'editor.glossary.createSubtitle': {
    en: 'Select the language pair and name your glossary',
    es: 'Selecciona el par de idiomas y ponle un nombre al diccionario',
    pt: 'Selecione o par de idiomas e dê um nome ao dicionário',
    fr: 'Sélectionnez la paire de langues et nommez votre dictionnaire',
    it: 'Seleziona la coppia di lingue e assegna un nome al dizionario',
    ru: 'Выберите языковую пару и укажите название словаря',
  },
  'editor.glossary.dictionary': {
    en: 'Glossary',
    es: 'Diccionario',
    pt: 'Dicionário',
    fr: 'Dictionnaire',
    it: 'Dizionario',
    ru: 'Словарь',
  },
  'editor.glossary.upgradeToCreate': {
    en: 'Upgrade to create a dictionary',
    es: 'Mejora tu plan para crear un diccionario',
    pt: 'Faça upgrade para criar um dicionário',
    fr: 'Passez à une offre supérieure pour créer un dictionnaire',
    it: 'Passa a un piano superiore per creare un dizionario',
    ru: 'Повысьте тариф, чтобы создать словарь',
  },
  'editor.glossary.zeroStateDescription': {
    en: 'Specify your own translations for words and phrases, and use them consistently across all your projects.',
    es: 'Define tus propias traducciones para palabras y frases y úsalas de forma coherente en todos tus proyectos.',
    pt: 'Defina suas próprias traduções para palavras e frases e use-as de forma consistente em todos os projetos.',
    fr: 'Définissez vos propres traductions de mots et d’expressions et utilisez-les de manière cohérente dans tous vos projets.',
    it: 'Definisci le tue traduzioni per parole e frasi e usale in modo coerente in tutti i progetti.',
    ru: 'Задавайте собственные переводы слов и фраз и последовательно используйте их во всех проектах.',
  },
  'editor.glossary.dictionaries': {
    en: 'Dictionaries',
    es: 'Diccionarios',
    pt: 'Dicionários',
    fr: 'Dictionnaires',
    it: 'Dizionari',
    ru: 'Словари',
  },
  'editor.glossary.doNotTranslate': {
    en: 'Do not translate',
    es: 'No traducir',
    pt: 'Não traduzir',
    fr: 'Ne pas traduire',
    it: 'Non tradurre',
    ru: 'Не переводить',
  },
  'editor.glossary.downloadTemplate': {
    en: 'Download template',
    es: 'Descargar plantilla',
    pt: 'Baixar modelo',
    fr: 'Télécharger le modèle',
    it: 'Scarica modello',
    ru: 'Скачать шаблон',
  },
  'editor.glossary.enterName': {
    en: 'Enter name',
    es: 'Introduce un nombre',
    pt: 'Digite o nome',
    fr: 'Saisissez un nom',
    it: 'Inserisci nome',
    ru: 'Введите название',
  },
  'editor.glossary.importHint': {
    en: 'Import your glossary from a CSV or TSV file. Download the template to get started quickly.',
    es: 'Importa tu diccionario desde un archivo CSV o TSV. Descarga la plantilla para usar un formato listo.',
    pt: 'Importe seu dicionário de um arquivo CSV ou TSV. Baixe o modelo para usar um formato pronto.',
    fr: 'Importez votre dictionnaire depuis un fichier CSV ou TSV. Téléchargez le modèle pour utiliser un format prêt à l’emploi.',
    it: 'Importa il dizionario da un file CSV o TSV. Scarica il modello per usare un formato già pronto.',
    ru: 'Импортируйте словарь из файла CSV или TSV. Скачайте шаблон с готовым форматом.',
  },
  'editor.glossary.languagePairUnavailable': {
    en: 'Translation dictionary is not available with this language pair',
    es: 'El diccionario de traducción no está disponible para este par de idiomas',
    pt: 'O dicionário de tradução não está disponível para este par de idiomas',
    fr: 'Le dictionnaire de traduction n’est pas disponible pour cette paire de langues',
    it: 'Il dizionario di traduzione non è disponibile per questa coppia di lingue',
    ru: 'Словарь перевода недоступен для этой языковой пары',
  },
  'editor.glossary.newVersionSubtitle': {
    en: 'The dictionary used in this project has been modified. Retranslate the project to apply changes.',
    es: 'El diccionario usado en este proyecto ha cambiado. Vuelve a traducir el proyecto para aplicar los cambios.',
    pt: 'O dicionário usado neste projeto foi alterado. Retraduza o projeto para aplicar as alterações.',
    fr: 'Le dictionnaire utilisé dans ce projet a été modifié. Retraduisez le projet pour appliquer les changements.',
    it: 'Il dizionario usato in questo progetto è stato modificato. Ritraduci il progetto per applicare le modifiche.',
    ru: 'Словарь, используемый в проекте, изменился. Переведите проект заново, чтобы применить изменения.',
  },
  'editor.glossary.newVersionTitle': {
    en: 'New version of the dictionary is available',
    es: 'Hay una nueva versión del diccionario',
    pt: 'Uma nova versão do dicionário está disponível',
    fr: 'Une nouvelle version du dictionnaire est disponible',
    it: 'È disponibile una nuova versione del dizionario',
    ru: 'Доступна новая версия словаря',
  },
  'editor.glossary.deleteSubtitle': {
    en: 'Once deleted, this dictionary will no longer be accessible.',
    es: 'Una vez eliminado, ya no podrás acceder a este diccionario.',
    pt: 'Depois de excluído, este dicionário não estará mais acessível.',
    fr: 'Une fois supprimé, ce dictionnaire ne sera plus accessible.',
    it: 'Dopo l’eliminazione, il dizionario non sarà più accessibile.',
    ru: 'После удаления словарь будет недоступен.',
  },
  'editor.glossary.deleteTitle': {
    en: 'Would you like to permanently delete this dictionary?',
    es: '¿Quieres eliminar este diccionario permanentemente?',
    pt: 'Deseja excluir este dicionário permanentemente?',
    fr: 'Voulez-vous supprimer définitivement ce dictionnaire ?',
    it: 'Vuoi eliminare definitivamente questo dizionario?',
    ru: 'Удалить этот словарь без возможности восстановления?',
  },
  'editor.glossary.name': { en: 'Name', es: 'Nombre', pt: 'Nome', fr: 'Nom', it: 'Nome', ru: 'Название' },
  'editor.glossary.noMoreForProject': {
    en: "Don't show this again for the current project",
    es: 'No volver a mostrar para este proyecto',
    pt: 'Não mostrar novamente neste projeto',
    fr: 'Ne plus afficher pour ce projet',
    it: 'Non mostrare più per questo progetto',
    ru: 'Больше не показывать для этого проекта',
  },
  'editor.glossary.off': {
    en: 'Off',
    es: 'Desactivado',
    pt: 'Desativado',
    fr: 'Désactivé',
    it: 'Disattivato',
    ru: 'Выкл.',
  },
  'editor.glossary.oldVersion': {
    en: 'Old version in the project',
    es: 'Versión antigua en el proyecto',
    pt: 'Versão antiga no projeto',
    fr: 'Ancienne version dans le projet',
    it: 'Versione precedente nel progetto',
    ru: 'В проекте используется старая версия',
  },
  'editor.glossary.retranslateSubtitle': {
    en: 'The glossary used in the project has been modified. To apply the changes, retranslate the project.',
    es: 'El diccionario usado en el proyecto ha cambiado. Para aplicar los cambios, es necesario volver a traducir el proyecto.',
    pt: 'O dicionário usado no projeto foi alterado. É necessário retraduzir para aplicar as alterações.',
    fr: 'Le dictionnaire utilisé dans le projet a été modifié. Une retraduction est nécessaire pour appliquer les changements.',
    it: 'Il dizionario usato nel progetto è stato modificato. Per applicare le modifiche è necessario ritradurre.',
    ru: 'Словарь, используемый в проекте, изменился. Чтобы применить изменения, нужно перевести проект заново.',
  },
  'editor.glossary.retranslateTitle': {
    en: 'Do you want to apply changes and retranslate the project?',
    es: '¿Quieres aplicar los cambios y volver a traducir el proyecto?',
    pt: 'Deseja aplicar as alterações e retraduzir o projeto?',
    fr: 'Voulez-vous appliquer les changements et retraduire le projet ?',
    it: 'Vuoi applicare le modifiche e ritradurre il progetto?',
    ru: 'Применить изменения и перевести проект заново?',
  },
  'editor.glossary.rename': {
    en: 'Rename Dictionary',
    es: 'Cambiar nombre del diccionario',
    pt: 'Renomear dicionário',
    fr: 'Renommer le dictionnaire',
    it: 'Rinomina dizionario',
    ru: 'Переименовать словарь',
  },
  'editor.glossary.saveAndContinue': {
    en: 'Save and continue',
    es: 'Guardar y continuar',
    pt: 'Salvar e continuar',
    fr: 'Enregistrer et continuer',
    it: 'Salva e continua',
    ru: 'Сохранить и продолжить',
  },
  'editor.glossary.searchLanguage': {
    en: 'Search for language',
    es: 'Buscar idioma',
    pt: 'Buscar idioma',
    fr: 'Rechercher une langue',
    it: 'Cerca lingua',
    ru: 'Найти язык',
  },
  'editor.glossary.selectLanguage': {
    en: 'Select language',
    es: 'Seleccionar idioma',
    pt: 'Selecionar idioma',
    fr: 'Sélectionner une langue',
    it: 'Seleziona lingua',
    ru: 'Выберите язык',
  },
  'editor.glossary.typeTranslation': {
    en: 'Type translation',
    es: 'Escribe la traducción',
    pt: 'Digite a tradução',
    fr: 'Saisissez la traduction',
    it: 'Inserisci la traduzione',
    ru: 'Введите перевод',
  },
  'editor.glossary.typeWord': {
    en: 'Type word',
    es: 'Escribe una palabra',
    pt: 'Digite a palavra',
    fr: 'Saisissez un mot',
    it: 'Inserisci una parola',
    ru: 'Введите слово',
  },
  'editor.glossary.unsavedSubtitle': {
    en: 'If you exit the glossary now, your latest translations will be lost. Save the changes and continue.',
    es: 'Si sales ahora del diccionario, perderás las últimas traducciones. Guarda los cambios para continuar.',
    pt: 'Se você sair do dicionário agora, perderá as traduções mais recentes. Salve as alterações e continue.',
    fr: 'Si vous quittez le dictionnaire maintenant, vos dernières traductions seront perdues. Enregistrez les changements pour continuer.',
    it: 'Se esci ora dal dizionario, perderai le ultime traduzioni. Salva le modifiche per continuare.',
    ru: 'Если выйти из словаря сейчас, последние переводы будут потеряны. Сохраните изменения, чтобы продолжить.',
  },
  'editor.glossary.unsavedTitle': {
    en: 'You have unsaved changes',
    es: 'Tienes cambios sin guardar',
    pt: 'Há alterações não salvas',
    fr: 'Vous avez des modifications non enregistrées',
    it: 'Hai modifiche non salvate',
    ru: 'Есть несохранённые изменения',
  },
  'editor.header.getHelp': {
    en: 'Get help',
    es: 'Obtener ayuda',
    pt: 'Obter ajuda',
    fr: 'Obtenir de l’aide',
    it: 'Richiedi assistenza',
    ru: 'Получить помощь',
  },
  'editor.header.helpCenter': {
    en: 'Help center',
    es: 'Centro de ayuda',
    pt: 'Central de ajuda',
    fr: 'Centre d’aide',
    it: 'Centro assistenza',
    ru: 'Справочный центр',
  },
  'editor.header.translateOwnVideo': {
    en: 'Translate my own video',
    es: 'Traducir mi propio video',
    pt: 'Traduzir meu próprio vídeo',
    fr: 'Traduire ma propre vidéo',
    it: 'Traduci il mio video',
    ru: 'Перевести своё видео',
  },
  'editor.language.infoAria': {
    en: 'Voice Clone language details',
    es: 'Información',
    pt: 'Informações',
    fr: 'Informations',
    it: 'Informazioni',
    ru: 'Сведения о языке клонирования голоса',
  },
  'editor.language.starsAria': {
    en: 'Voice Clone available',
    es: 'Función de IA',
    pt: 'Recurso de IA',
    fr: 'Fonctionnalité IA',
    it: 'Funzione IA',
    ru: 'Клонирование голоса доступно',
  },
  'editor.language.translation': {
    en: 'Translation: {language}',
    es: 'Traducción: {language}',
    pt: 'Tradução: {language}',
    fr: 'Traduction : {language}',
    it: 'Traduzione: {language}',
    ru: 'Перевод: {language}',
  },
  'editor.language.voiceClone': {
    en: 'Voice Clone',
    es: 'Clon de voz',
    pt: 'Clonagem de voz',
    fr: 'Clonage vocal',
    it: 'Clonazione vocale',
    ru: 'Клонирование голоса',
  },
  'editor.language.voiceCloneLanguage': {
    en: 'Voice Clone: {language}',
    es: 'Clon de voz: {language}',
    pt: 'Clonagem de voz: {language}',
    fr: 'Clonage vocal : {language}',
    it: 'Clonazione vocale: {language}',
    ru: 'Клонирование голоса: {language}',
  },
  'editor.mobile.adjustTimestamps': {
    en: 'Adjust timestamps',
    es: 'Ajustar marcas de tiempo',
    pt: 'Ajustar marcações de tempo',
    fr: 'Ajuster les horodatages',
    it: 'Regola i timestamp',
    ru: 'Настраивать таймкоды',
  },
  'editor.mobile.assignSpeakers': {
    en: 'Assign speakers and voices',
    es: 'Asignar hablantes y voces',
    pt: 'Atribuir falantes e vozes',
    fr: 'Attribuer les locuteurs et les voix',
    it: 'Assegna locutori e voci',
    ru: 'Назначать спикеров и голоса',
  },
  'editor.mobile.backHome': {
    en: 'Back to Home',
    es: 'Volver al inicio',
    pt: 'Voltar ao início',
    fr: 'Retour à l’accueil',
    it: 'Torna alla home',
    ru: 'На главную',
  },
  'editor.mobile.desktopHint': {
    en: 'Please open the project on your desktop to explore all editor features.',
    es: 'Abre el proyecto en un ordenador para explorar todas las funciones del editor.',
    pt: 'Abra o projeto no computador para explorar todos os recursos do editor.',
    fr: 'Ouvrez le projet sur ordinateur pour découvrir toutes les fonctions de l’éditeur.',
    it: 'Apri il progetto da computer per esplorare tutte le funzioni dell’editor.',
    ru: 'Откройте проект на компьютере, чтобы использовать все возможности редактора.',
  },
  'editor.mobile.firecrackerAria': {
    en: 'Party popper',
    es: 'Lanzador de confeti',
    pt: 'Lança-confetes',
    fr: 'Canon à confettis',
    it: 'Sparacoriandoli',
    ru: 'Хлопушка',
  },
  'editor.mobile.goatAria': {
    en: 'Rock on gesture',
    es: 'Gesto de rock',
    pt: 'Gesto de rock',
    fr: 'Geste rock',
    it: 'Gesto rock',
    ru: 'Жест «коза»',
  },
  'editor.mobile.laptopAria': {
    en: 'Laptop',
    es: 'Portátil',
    pt: 'Notebook',
    fr: 'Ordinateur portable',
    it: 'Computer portatile',
    ru: 'Ноутбук',
  },
  'editor.mobile.phoneAria': {
    en: 'Mobile phone',
    es: 'Teléfono móvil',
    pt: 'Celular',
    fr: 'Téléphone mobile',
    it: 'Telefono cellulare',
    ru: 'Мобильный телефон',
  },
  'editor.mobile.deleteAll': {
    en: 'Delete all ({count})',
    es: 'Eliminar todos ({count})',
    pt: 'Excluir todos ({count})',
    fr: 'Tout supprimer ({count})',
    it: 'Elimina tutti ({count})',
    ru: 'Удалить все ({count})',
  },
  'editor.mobile.dubDescription': {
    en: "By pressing the Dub button, we'll automatically deduct minutes from your plan according to the video's duration",
    es: 'Al pulsar el botón Doblar, descontaremos automáticamente los minutos de tu plan según la duración del video',
    pt: 'Ao clicar em Dublar, os minutos serão descontados automaticamente do seu plano de acordo com a duração do vídeo',
    fr: 'En appuyant sur le bouton « Doubler la vidéo », les minutes correspondant à la durée de la vidéo seront automatiquement déduites de votre forfait',
    it: 'Premendo il pulsante «Doppia il video», i minuti verranno detratti automaticamente dal piano in base alla durata del video',
    ru: 'После нажатия «Озвучить» минуты автоматически спишутся с тарифа в соответствии с длительностью видео',
  },
  'editor.mobile.dubTitle': {
    en: 'Dub here or open on desktop to check the transcript and translation',
    es: 'Dobla aquí o abre el proyecto en un ordenador para revisar la transcripción y la traducción',
    pt: 'Duble aqui ou abra no computador para revisar a transcrição e a tradução',
    fr: 'Doublez ici ou ouvrez le projet sur ordinateur pour vérifier la transcription et la traduction',
    it: 'Doppia qui oppure apri il progetto da computer per controllare trascrizione e traduzione',
    ru: 'Озвучьте здесь или откройте проект на компьютере, чтобы проверить транскрипцию и перевод',
  },
  'editor.mobile.dubVideo': {
    en: 'Dub video',
    es: 'Doblar video',
    pt: 'Dublar vídeo',
    fr: 'Doubler la vidéo',
    it: 'Doppia il video',
    ru: 'Озвучить видео',
  },
  'editor.mobile.editorDesktopOnly': {
    en: 'The editor is only available on desktop',
    es: 'El editor solo está disponible en ordenador',
    pt: 'O editor está disponível apenas no computador',
    fr: 'L’éditeur est uniquement disponible sur ordinateur',
    it: 'L’editor è disponibile solo da computer',
    ru: 'Редактор доступен только на компьютере',
  },
  'editor.mobile.emptySegments': {
    en: 'Fill in or delete empty segments to dub the video.',
    es: 'Completa o elimina los segmentos vacíos para doblar el video.',
    pt: 'Preencha ou exclua os segmentos vazios para dublar o vídeo.',
    fr: 'Remplissez ou supprimez les segments vides pour doubler la vidéo.',
    it: 'Compila o elimina i segmenti vuoti per doppiare il video.',
    ru: 'Заполните или удалите пустые сегменты, чтобы озвучить видео.',
  },
  'editor.mobile.resultDescription': {
    en: 'You can check out the result, compare it with the original, download it, or improve it in our easy-to-use editor',
    es: 'Puedes ver el resultado, compararlo con el original, descargarlo o mejorarlo en nuestro sencillo editor',
    pt: 'Você pode conferir o resultado, compará-lo com o original, baixá-lo ou melhorá-lo em nosso editor fácil de usar',
    fr: 'Vous pouvez consulter le résultat, le comparer à l’original, le télécharger ou l’améliorer dans notre éditeur intuitif',
    it: 'Puoi vedere il risultato, confrontarlo con l’originale, scaricarlo o migliorarlo nel nostro editor intuitivo',
    ru: 'Посмотрите результат, сравните его с оригиналом, скачайте или улучшите в нашем удобном редакторе',
  },
  'editor.mobile.translateOwn': {
    en: 'Translate my own video',
    es: 'Traducir mi propio video',
    pt: 'Traduzir meu próprio vídeo',
    fr: 'Traduire ma propre vidéo',
    it: 'Traduci il mio video',
    ru: 'Перевести своё видео',
  },
  'editor.mobile.videoReady': {
    en: 'Your video is ready!',
    es: '¡Tu video está listo!',
    pt: 'Seu vídeo está pronto!',
    fr: 'Votre vidéo est prête !',
    it: 'Il tuo video è pronto!',
    ru: 'Видео готово!',
  },
  'editor.notification.videoGenerated': {
    en: 'Video generated',
    es: 'El video se ha generado',
    pt: 'O vídeo foi gerado',
    fr: 'La vidéo a été générée',
    it: 'Il video è stato generato',
    ru: 'Видео готово',
  },
  'editor.mobile.editText': {
    en: 'Edit transcription and translation text',
    es: 'Editar la transcripción y la traducción',
    pt: 'Editar o texto da transcrição e da tradução',
    fr: 'Modifier la transcription et la traduction',
    it: 'Modifica trascrizione e traduzione',
    ru: 'Редактировать транскрипцию и перевод',
  },
  'editor.mobile.fullExperience': {
    en: 'Get the full experience on desktop',
    es: 'Disfruta de la experiencia completa en un ordenador',
    pt: 'Tenha a experiência completa no computador',
    fr: 'Profitez de toutes les fonctionnalités sur ordinateur',
    it: 'Scopri tutte le funzioni nella versione desktop',
    ru: 'Все возможности доступны на компьютере',
  },
  'editor.mobile.leaveAndReturn': {
    en: 'Feel free to leave the page and come back later.',
    es: 'Puedes salir de la página y volver más tarde.',
    pt: 'Você pode sair da página e voltar mais tarde.',
    fr: 'Vous pouvez quitter la page et revenir plus tard.',
    it: 'Puoi uscire dalla pagina e tornare più tardi.',
    ru: 'Можно закрыть страницу и вернуться позже.',
  },
  'editor.mobile.leaveOrWait': {
    en: 'You can leave this page or wait here',
    es: 'Puedes salir de esta página o esperar aquí',
    pt: 'Você pode sair desta página ou esperar aqui',
    fr: 'Vous pouvez quitter cette page ou patienter ici',
    it: 'Puoi uscire da questa pagina o attendere qui',
    ru: 'Можно покинуть страницу или подождать здесь',
  },
  'editor.mobile.uploadSrt': {
    en: 'Upload SRT file',
    es: 'Subir archivo SRT',
    pt: 'Enviar arquivo SRT',
    fr: 'Importer un fichier SRT',
    it: 'Carica un file SRT',
    ru: 'Загружать файл SRT',
  },
  'editor.multilanguage.availableLanguages': {
    en: 'Available languages',
    es: 'Idiomas disponibles',
    pt: 'Idiomas disponíveis',
    fr: 'Langues disponibles',
    it: 'Lingue disponibili',
    ru: 'Доступные языки',
  },
  'editor.multilanguage.autoDub': {
    en: 'Auto-dub',
    es: 'Doblaje automático',
    pt: 'Dublagem automática',
    fr: 'Doublage automatique',
    it: 'Doppiaggio automatico',
    ru: 'Автоозвучка',
  },
  'editor.multilanguage.autoDubHint': {
    en: 'Generate a dubbed video automatically.',
    es: 'Genera automáticamente un video doblado.',
    pt: 'Gere automaticamente um vídeo dublado.',
    fr: 'Générez automatiquement une vidéo doublée.',
    it: 'Genera automaticamente un video doppiato.',
    ru: 'Автоматически создавать озвученное видео.',
  },
  'editor.multilanguage.choose': {
    en: 'Choose languages for translation',
    es: 'Elige los idiomas a los que quieres traducir',
    pt: 'Escolha os idiomas para tradução',
    fr: 'Choisissez les langues de traduction',
    it: 'Scegli le lingue per la traduzione',
    ru: 'Выберите языки перевода',
  },
  'editor.multilanguage.clearAll': {
    en: 'Clear all',
    es: 'Borrar todo',
    pt: 'Limpar tudo',
    fr: 'Tout effacer',
    it: 'Deseleziona tutto',
    ru: 'Очистить всё',
  },
  'editor.multilanguage.collapse': {
    en: 'Collapse',
    es: 'Contraer',
    pt: 'Recolher',
    fr: 'Réduire',
    it: 'Comprimi',
    ru: 'Свернуть',
  },
  'editor.multilanguage.expand': {
    en: 'Expand',
    es: 'Expandir',
    pt: 'Expandir',
    fr: 'Déplier',
    it: 'Espandi',
    ru: 'Развернуть',
  },
  'editor.multilanguage.faqSuffix': {
    en: 'about how to use this feature effectively.',
    es: 'sobre cómo usar esta función eficazmente.',
    pt: 'sobre como usar este recurso de forma eficaz.',
    fr: 'sur la façon d’utiliser efficacement cette fonctionnalité.',
    it: 'su come usare al meglio questa funzione.',
    ru: 'о том, как эффективно использовать эту функцию.',
  },
  'editor.multilanguage.goToProjectList': {
    en: 'Go to Project List',
    es: 'Ir a la lista de proyectos',
    pt: 'Ir para a lista de projetos',
    fr: 'Accéder à la liste des projets',
    it: 'Vai all’elenco dei progetti',
    ru: 'К списку проектов',
  },
  'editor.multilanguage.openProject': {
    en: 'Open project',
    es: 'Abrir proyecto',
    pt: 'Abrir projeto',
    fr: 'Ouvrir le projet',
    it: 'Apri progetto',
    ru: 'Открыть проект',
  },
  'editor.multilanguage.reuseTranscript': {
    en: "We'll reuse the original video transcription so you don't need to proofread it twice.",
    es: 'Reutilizaremos la transcripción original para que no tengas que revisarla dos veces.',
    pt: 'Reutilizaremos a transcrição original para você não precisar revisá-la duas vezes.',
    fr: 'Nous réutiliserons la transcription d’origine pour éviter une double relecture.',
    it: 'Riutilizzeremo la trascrizione originale, così non dovrai correggerla due volte.',
    ru: 'Мы используем исходную транскрипцию, чтобы вам не пришлось проверять её дважды.',
  },
  'editor.multilanguage.search': {
    en: 'Search languages',
    es: 'Buscar idiomas',
    pt: 'Pesquisar idiomas',
    fr: 'Rechercher des langues',
    it: 'Cerca lingue',
    ru: 'Поиск языков',
  },
  'editor.multilanguage.select': {
    en: 'Select languages',
    es: 'Seleccionar idiomas',
    pt: 'Selecionar idiomas',
    fr: 'Sélectionner les langues',
    it: 'Seleziona lingue',
    ru: 'Выберите языки',
  },
  'editor.multilanguage.submitting': {
    en: 'Submitting...',
    es: 'Enviando...',
    pt: 'Enviando...',
    fr: 'Envoi…',
    it: 'Invio...',
    ru: 'Отправка…',
  },
  'editor.multilanguage.successSubtitle': {
    en: 'We created new projects for the selected languages. Open any project below, or stay in the current project.',
    es: 'Hemos creado proyectos para los idiomas seleccionados. Abre uno de ellos a continuación o quédate en el proyecto actual.',
    pt: 'Criamos novos projetos para os idiomas selecionados. Abra um projeto abaixo ou continue no projeto atual.',
    fr: 'Nous avons créé de nouveaux projets pour les langues sélectionnées. Ouvrez l’un des projets ci-dessous ou restez dans le projet actuel.',
    it: 'Abbiamo creato nuovi progetti per le lingue selezionate. Apri uno dei progetti qui sotto o rimani nel progetto corrente.',
    ru: 'Мы создали новые проекты для выбранных языков. Откройте любой из них или останьтесь в текущем проекте.',
  },
  'editor.multilanguage.successTitle': {
    en: 'Your translation projects are ready',
    es: 'Tus proyectos de traducción están listos',
    pt: 'Seus projetos de tradução estão prontos',
    fr: 'Vos projets de traduction sont prêts',
    it: 'I tuoi progetti di traduzione sono pronti',
    ru: 'Проекты с переводом готовы',
  },
  'editor.multilanguage.stayHere': {
    en: 'Stay here',
    es: 'Quedarse aquí',
    pt: 'Ficar aqui',
    fr: 'Rester ici',
    it: 'Rimani qui',
    ru: 'Остаться здесь',
  },
  'editor.multilanguage.translateTo': {
    en: 'Translate to',
    es: 'Traducir a',
    pt: 'Traduzir para',
    fr: 'Traduire vers',
    it: 'Traduci in',
    ru: 'Перевести на',
  },
  'editor.multilanguage.voiceHintMany': {
    en: '{count} languages · voiceover: AI presets{voiceClone}',
    es: '{count} idiomas · locución: voces predefinidas de IA{voiceClone}',
    pt: '{count} idiomas · locução: vozes de IA predefinidas{voiceClone}',
    fr: '{count} langues · voix off : préréglages IA{voiceClone}',
    it: '{count} lingue · doppiaggio: voci IA preimpostate{voiceClone}',
    ru: 'Выбрано языков: {count} · озвучка: голоса ИИ{voiceClone}',
  },
  'editor.multilanguage.voiceHintOne': {
    en: 'Translation: {language}, voiceover: AI presets{voiceClone}',
    es: 'Traducción: {language}, locución: voces predefinidas de IA{voiceClone}',
    pt: 'Tradução: {language}, locução: vozes de IA predefinidas{voiceClone}',
    fr: 'Traduction : {language}, voix off : préréglages IA{voiceClone}',
    it: 'Traduzione: {language}, doppiaggio: voci IA preimpostate{voiceClone}',
    ru: 'Перевод: {language}, озвучка: голоса ИИ{voiceClone}',
  },
  'editor.multilanguage.voiceCloneOr': {
    en: ' or voice clone',
    es: ' o clon de voz',
    pt: ' ou clone de voz',
    fr: ' ou clone vocal',
    it: ' o clone vocale',
    ru: ' или клон голоса',
  },
  'editor.multilanguage.voiceCloneOrNamed': {
    en: ' or {name} voice clone',
    es: ' o clon de la voz {name}',
    pt: ' ou clone de voz {name}',
    fr: ' ou clone vocal {name}',
    it: ' o clone vocale {name}',
    ru: ' или клон голоса {name}',
  },
  'editor.onboarding.advancedToolsSubtitle': {
    en: 'Access features like the Dictionary, Translation prompting, and SRT File Upload to enhance your translation workflow.',
    es: 'Accede a funciones como el diccionario, las instrucciones de traducción y la carga de archivos SRT para mejorar tu proceso de traducción.',
    pt: 'Acesse recursos como Dicionário, instruções de tradução e envio de arquivos SRT para aprimorar seu fluxo de tradução.',
    fr: 'Accédez au dictionnaire, aux consignes de traduction et à l’importation de fichiers SRT pour améliorer votre flux de traduction.',
    it: 'Accedi a funzioni come il Dizionario, i prompt di traduzione e il caricamento di file SRT per migliorare il flusso di traduzione.',
    ru: 'Используйте словарь, промпты для перевода и загрузку SRT-файлов, чтобы сделать работу над переводом удобнее.',
  },
  'editor.onboarding.advancedToolsTitle': {
    en: 'Advanced tools for power users',
    es: 'Herramientas avanzadas para usuarios expertos',
    pt: 'Ferramentas avançadas para usuários experientes',
    fr: 'Outils avancés pour les utilisateurs expérimentés',
    it: 'Strumenti avanzati per utenti esperti',
    ru: 'Расширенные инструменты для опытных пользователей',
  },
  'editor.onboarding.audioTrackTitle': {
    en: 'Adjust timestamps to synchronize the translated and original speech',
    es: 'Ajusta los códigos de tiempo para sincronizar el audio traducido con el original',
    pt: 'Ajuste as marcações de tempo para sincronizar a fala traduzida com a original',
    fr: 'Ajustez les horodatages pour synchroniser l’audio traduit avec l’original',
    it: 'Regola i timestamp per sincronizzare il parlato tradotto con quello originale',
    ru: 'Настройте таймкоды, чтобы синхронизировать перевод с оригинальной речью',
  },
  'editor.onboarding.changeTextTitle': {
    en: 'Edit text, speakers, and voices here',
    es: 'Edita aquí el texto, los hablantes y la voz',
    pt: 'Edite aqui o texto, os falantes e a voz',
    fr: 'Modifiez ici le texte, les locuteurs et la voix',
    it: 'Modifica qui testo, locutori e voci',
    ru: 'Здесь можно изменить текст, спикеров и голос',
  },
  'editor.onboarding.clickPlayTitle': {
    en: 'Click play to see your new translated video',
    es: 'Pulsa reproducir para ver tu nuevo video traducido',
    pt: 'Clique em reproduzir para ver seu novo vídeo traduzido',
    fr: 'Cliquez sur Lecture pour voir votre nouvelle vidéo traduite',
    it: 'Premi Riproduci per vedere il nuovo video tradotto',
    ru: 'Нажмите «Воспроизвести», чтобы посмотреть переведённое видео',
  },
  'editor.onboarding.customizeVoiceTitle': {
    en: 'Customize your Voice Clone by adjusting expressiveness and similarity levels',
    es: 'Personaliza tu clon de voz ajustando los niveles de expresividad y similitud',
    pt: 'Personalize seu clone de voz ajustando os níveis de expressividade e semelhança',
    fr: 'Personnalisez votre clone vocal en réglant les niveaux d’expressivité et de similarité',
    it: 'Personalizza il clone vocale regolando i livelli di espressività e somiglianza',
    ru: 'Настройте клон голоса, изменив выразительность и сходство',
  },
  'editor.onboarding.demoWelcomeSubtitle': {
    en: 'Explore the original and translated versions of the video here. Experiment with the editor to get a feel for how it works!',
    es: 'Aquí puedes explorar las versiones original y traducida del video. ¡Prueba el editor para descubrir cómo funciona!',
    pt: 'Explore aqui as versões original e traduzida do vídeo. Experimente o editor para ver como ele funciona!',
    fr: 'Explorez ici les versions originale et traduite de la vidéo. Essayez l’éditeur pour découvrir son fonctionnement !',
    it: 'Esplora qui le versioni originale e tradotta del video. Prova l’editor per scoprire come funziona!',
    ru: 'Здесь можно посмотреть оригинал и перевод видео. Поэкспериментируйте с редактором, чтобы разобраться, как он работает.',
  },
  'editor.onboarding.demoWelcomeTitle': {
    en: 'Welcome to the demo project',
    es: 'Te damos la bienvenida al proyecto de demostración',
    pt: 'Bem-vindo ao projeto de demonstração',
    fr: 'Bienvenue dans le projet de démonstration',
    it: 'Ti diamo il benvenuto nel progetto demo',
    ru: 'Добро пожаловать в демопроект',
  },
  'editor.onboarding.discoverTeamspaceSubtitle': {
    en: 'Manage and collaborate with your team effortlessly by creating new Teamspaces and switching between them here.',
    es: 'Gestiona y colabora con tu equipo fácilmente creando nuevos espacios de equipo y cambiando entre ellos aquí.',
    pt: 'Gerencie e colabore com sua equipe facilmente criando novos Teamspaces e alternando entre eles aqui.',
    fr: 'Gérez facilement la collaboration avec votre équipe en créant de nouveaux Teamspaces et en passant de l’un à l’autre ici.',
    it: 'Gestisci i Teamspace e collabora facilmente con il team creandone di nuovi e passando qui da uno all’altro.',
    ru: 'Создавайте командные пространства и переключайтесь между ними, чтобы удобно управлять работой вместе с командой.',
  },
  'editor.onboarding.discoverTeamspaceTitle': {
    en: 'Discover Teamspaces',
    es: 'Descubre los espacios de equipo',
    pt: 'Conheça os Teamspaces',
    fr: 'Découvrez les Teamspaces',
    it: 'Scopri i Teamspace',
    ru: 'Познакомьтесь с командными пространствами',
  },
  'editor.onboarding.downloadTitle': {
    en: 'Download video here',
    es: 'Descarga el video aquí',
    pt: 'Baixe o vídeo aqui',
    fr: 'Téléchargez la vidéo ici',
    it: 'Scarica qui il video',
    ru: 'Здесь можно скачать видео',
  },
  'editor.onboarding.manageTeamspaceSubtitle': {
    en: 'Easily switch between spaces right here to collaborate with your team.',
    es: 'Cambia fácilmente de espacio aquí para colaborar con tu equipo.',
    pt: 'Alterne facilmente entre os espaços aqui para colaborar com sua equipe.',
    fr: 'Passez facilement d’un espace à l’autre ici pour collaborer avec votre équipe.',
    it: 'Passa facilmente da uno spazio all’altro qui per collaborare con il team.',
    ru: 'Здесь можно легко переключаться между пространствами и работать вместе с командой.',
  },
  'editor.onboarding.manageTeamspaceTitle': {
    en: 'Manage your Teamspaces here',
    es: 'Gestiona aquí tus espacios de equipo',
    pt: 'Gerencie seus Teamspaces aqui',
    fr: 'Gérez vos Teamspaces ici',
    it: 'Gestisci qui i tuoi Teamspace',
    ru: 'Здесь можно управлять командными пространствами',
  },
  'editor.onboarding.segmentPreviewSubtitle': {
    en: 'Use row playback to check timing and voice before applying changes to the full project.',
    es: 'Reproduce la fila para comprobar los tiempos y la voz antes de aplicar los cambios a todo el proyecto.',
    pt: 'Reproduza a linha para verificar a sincronização e a voz antes de aplicar as alterações ao projeto inteiro.',
    fr: 'Utilisez le bouton de lecture de la ligne pour vérifier la synchronisation et la voix avant d’appliquer les modifications à l’ensemble du projet.',
    it: 'Riproduci la riga per controllare tempi e voce prima di applicare le modifiche all’intero progetto.',
    ru: 'Воспроизведите строку, чтобы проверить тайминг и голос перед применением изменений ко всему проекту.',
  },
  'editor.onboarding.segmentPreviewTitle': {
    en: 'Preview a segment before dubbing the whole video',
    es: 'Previsualiza un segmento antes de doblar todo el video',
    pt: 'Pré-visualize um segmento antes de dublar o vídeo inteiro',
    fr: 'Prévisualisez un segment avant de doubler toute la vidéo',
    it: 'Visualizza l’anteprima di un segmento prima di doppiare l’intero video',
    ru: 'Прослушайте сегмент перед озвучкой всего видео',
  },
  'editor.onboarding.speakerVoicesTitle': {
    en: "Manage all speakers' voices and don't forget to apply changes",
    es: 'Gestiona las voces de todos los hablantes y no olvides aplicar los cambios',
    pt: 'Gerencie as vozes de todos os falantes e não se esqueça de aplicar as alterações',
    fr: 'Gérez les voix de tous les locuteurs et n’oubliez pas d’appliquer les modifications',
    it: 'Gestisci le voci di tutti i locutori e non dimenticare di applicare le modifiche',
    ru: 'Настройте голоса всех спикеров и не забудьте применить изменения',
  },
  'editor.onboarding.transcriptVersionsSubtitle': {
    en: 'Open version history to review changes, then use the rollback icon on an older version to restore it.',
    es: 'Abre el historial de versiones para revisar los cambios y usa el icono de restauración de una versión anterior para recuperarla.',
    pt: 'Abra o histórico de versões para revisar as alterações e use o ícone de reversão em uma versão anterior para restaurá-la.',
    fr: 'Ouvrez l’historique des versions pour examiner les modifications, puis utilisez l’icône de restauration d’une ancienne version pour la rétablir.',
    it: 'Apri la cronologia delle versioni per controllare le modifiche, quindi usa l’icona di ripristino su una versione precedente per ripristinarla.',
    ru: 'Откройте историю версий, чтобы просмотреть изменения, а затем нажмите значок отката у нужной версии.',
  },
  'editor.onboarding.transcriptVersionsTitle': {
    en: 'Save, compare, and roll back versions',
    es: 'Guarda, compara y restaura versiones',
    pt: 'Salve, compare e reverta versões',
    fr: 'Enregistrez, comparez et restaurez des versions',
    it: 'Salva, confronta e ripristina le versioni',
    ru: 'Сохраняйте, сравнивайте и восстанавливайте версии',
  },
  'editor.onboarding.translateOwnTitle': {
    en: 'Translate videos into 135+ languages',
    es: 'Traduce videos a más de 135 idiomas',
    pt: 'Traduza vídeos para mais de 135 idiomas',
    fr: 'Traduisez des vidéos dans plus de 135 langues',
    it: 'Traduci video in oltre 135 lingue',
    ru: 'Переводите видео на 135+ языков',
  },
  'editor.onboarding.translationVariantsSubtitle': {
    en: 'Pick a recommended or better-timed translation, then preview it in the row.',
    es: 'Elige una traducción recomendada o mejor sincronizada y previsualízala en la fila.',
    pt: 'Escolha uma tradução recomendada ou com melhor sincronização e visualize-a na linha.',
    fr: 'Choisissez une traduction recommandée ou mieux synchronisée, puis écoutez-la depuis la ligne.',
    it: 'Scegli una traduzione consigliata o con tempi migliori, quindi visualizzala in anteprima nella riga.',
    ru: 'Выберите рекомендованный или более точный по таймингу перевод, затем прослушайте его в строке.',
  },
  'editor.onboarding.translationVariantsTitle': {
    en: 'Try translation alternatives',
    es: 'Prueba traducciones alternativas',
    pt: 'Experimente alternativas de tradução',
    fr: 'Essayez d’autres traductions',
    it: 'Prova traduzioni alternative',
    ru: 'Попробуйте другие варианты перевода',
  },
  'editor.onboarding.videoTabsTitle': {
    en: 'Switch between original and translated videos',
    es: 'Cambia entre el video original y el traducido',
    pt: 'Alterne entre os vídeos original e traduzido',
    fr: 'Passez de la vidéo originale à la vidéo traduite',
    it: 'Passa dal video originale a quello tradotto e viceversa',
    ru: 'Переключайтесь между оригиналом и переводом видео',
  },
  'editor.processing.analyzingSpeakers': {
    en: 'Analyzing speakers',
    es: 'Analizando hablantes',
    pt: 'Analisando falantes',
    fr: 'Analyse des locuteurs',
    it: 'Analisi dei locutori',
    ru: 'Анализ спикеров',
  },
  'editor.processing.applyingChanges': {
    en: 'Applying changes',
    es: 'Aplicando cambios',
    pt: 'Aplicando alterações',
    fr: 'Application des modifications',
    it: 'Applicazione delle modifiche',
    ru: 'Применяем изменения',
  },
  'editor.processing.assigningVoiceToAddedSpeaker': {
    en: 'We are assigning a voice to the added speaker.',
    es: 'Estamos asignando una voz al hablante añadido.',
    pt: 'Estamos atribuindo uma voz ao locutor adicionado.',
    fr: 'Nous attribuons une voix au locuteur ajouté.',
    it: 'Stiamo assegnando una voce al locutore aggiunto.',
    ru: 'Назначаем голос добавленному спикеру.',
  },
  'editor.processing.analyzingSrt': {
    en: 'Analyzing SRT',
    es: 'Analizando SRT',
    pt: 'Analisando SRT',
    fr: 'Analyse du SRT',
    it: 'Analisi SRT',
    ru: 'Анализ SRT',
  },
  'editor.processing.analyzingVideo': {
    en: 'Analyzing video',
    es: 'Analizando video',
    pt: 'Analisando vídeo',
    fr: 'Analyse de la vidéo',
    it: 'Analisi del video',
    ru: 'Анализ видео',
  },
  'editor.processing.analyzingVoices': {
    en: 'Analyzing voices',
    es: 'Analizando voces',
    pt: 'Analisando vozes',
    fr: 'Analyse des voix',
    it: 'Analisi delle voci',
    ru: 'Анализ голосов',
  },
  'editor.processing.generatingAudio': {
    en: 'Generating audio',
    es: 'Generando audio',
    pt: 'Gerando áudio',
    fr: 'Génération de l’audio',
    it: 'Generazione audio',
    ru: 'Создание аудио',
  },
  'editor.processing.generatingVideo': {
    en: 'Generating video',
    es: 'Generando video',
    pt: 'Gerando vídeo',
    fr: 'Génération de la vidéo',
    it: 'Generazione video',
    ru: 'Создание видео',
  },
  'editor.processing.processing': {
    en: 'Processing',
    es: 'Procesando',
    pt: 'Processando',
    fr: 'Traitement',
    it: 'Elaborazione',
    ru: 'Обработка',
  },
  'editor.processing.saving': {
    en: 'Saving',
    es: 'Guardando',
    pt: 'Salvando',
    fr: 'Enregistrement',
    it: 'Salvataggio',
    ru: 'Сохранение',
  },
  'editor.processing.transcriptingVideo': {
    en: 'Transcribing video',
    es: 'Transcribiendo video',
    pt: 'Transcrevendo vídeo',
    fr: 'Transcription de la vidéo',
    it: 'Trascrizione video',
    ru: 'Транскрипция видео',
  },
  'editor.processing.translating': {
    en: 'Translating ({count})',
    es: 'Traduciendo ({count})',
    pt: 'Traduzindo ({count})',
    fr: 'Traduction ({count})',
    it: 'Traduzione ({count})',
    ru: 'Перевод ({count})',
  },
  'editor.processing.translatingText': {
    en: 'Translating text',
    es: 'Traduciendo texto',
    pt: 'Traduzindo texto',
    fr: 'Traduction du texte',
    it: 'Traduzione testo',
    ru: 'Перевод текста',
  },
  'editor.processing.unknown': {
    en: 'Processing ({count})',
    es: 'Procesando ({count})',
    pt: 'Processando ({count})',
    fr: 'Traitement ({count})',
    it: 'Elaborazione ({count})',
    ru: 'Обработка ({count})',
  },
  'editor.processing.uploadingVideo': {
    en: 'Uploading video',
    es: 'Subiendo video',
    pt: 'Enviando vídeo',
    fr: 'Importation de la vidéo',
    it: 'Caricamento video',
    ru: 'Загрузка видео',
  },
  'editor.trial.unlockFullAccess': {
    en: 'Unlock full access',
    es: 'Desbloquear acceso completo',
    pt: 'Desbloquear acesso completo',
    fr: 'Débloquer l’accès complet',
    it: 'Sblocca l’accesso completo',
    ru: 'Открыть полный доступ',
  },
  'editor.trial.videoCountOne': {
    en: '{count} video',
    es: '{count} video',
    pt: '{count} vídeo',
    fr: '{count} vidéo',
    it: '{count} video',
    ru: '{count} видео',
  },
  'editor.trial.videoCountFew': {
    en: '{count} videos',
    es: '{count} videos',
    pt: '{count} vídeos',
    fr: '{count} vidéos',
    it: '{count} video',
    ru: '{count} видео',
  },
  'editor.trial.videoCountMany': {
    en: '{count} videos',
    es: '{count} videos',
    pt: '{count} vídeos',
    fr: '{count} vidéos',
    it: '{count} video',
    ru: '{count} видео',
  },
  'editor.trial.videoCountOther': {
    en: '{count} videos',
    es: '{count} videos',
    pt: '{count} vídeos',
    fr: '{count} vidéos',
    it: '{count} video',
    ru: '{count} видео',
  },
  'editor.tutorial.getStartedSubtitle': {
    en: 'Discover the power of Rask for efficient video translation',
    es: 'Descubre todo el potencial de Rask para traducir videos con eficacia',
    pt: 'Descubra o potencial do Rask para traduzir vídeos com eficiência',
    fr: 'Découvrez toute la puissance de Rask pour traduire efficacement vos vidéos',
    it: 'Scopri la potenza di Rask per tradurre video in modo efficiente',
    ru: 'Узнайте, как Rask помогает эффективно переводить видео',
  },
  'editor.tutorial.getStartedTitle': {
    en: 'How to get started',
    es: 'Cómo empezar',
    pt: 'Como começar',
    fr: 'Bien démarrer',
    it: 'Come iniziare',
    ru: 'Как начать работу',
  },
  'editor.processing.voicing': {
    en: 'Voicing ({count})',
    es: 'Generando voz ({count})',
    pt: 'Gerando voz ({count})',
    fr: 'Génération des voix ({count})',
    it: 'Generazione voce ({count})',
    ru: 'Озвучка ({count})',
  },
  'editor.prompt.customizeSubtitle': {
    en: 'Customize the translation style, context, and tone of voice with prompting. Use a template or create your own prompt.',
    es: 'Personaliza el estilo, el contexto y el tono de la traducción con instrucciones. Usa una plantilla o crea tu propio prompt.',
    pt: 'Personalize o estilo, o contexto e o tom da tradução com instruções. Use um modelo ou crie seu próprio prompt.',
    fr: 'Personnalisez le style, le contexte et le ton de la traduction avec des consignes. Utilisez nos modèles ou créez le vôtre.',
    it: 'Personalizza lo stile, il contesto e il tono della traduzione con le istruzioni. Usa un modello o crea il tuo prompt.',
    ru: 'Настройте стиль, контекст и тон перевода с помощью промпта. Используйте шаблон или создайте свой.',
  },
  'editor.prompt.customizeTitle': {
    en: 'Customize translation',
    es: 'Personalizar traducción',
    pt: 'Personalizar tradução',
    fr: 'Personnaliser la traduction',
    it: 'Personalizza la traduzione',
    ru: 'Настройка перевода',
  },
  'editor.prompt.faqSuffix': {
    en: 'how to use prompting effectively',
    es: 'cómo usar los prompts de forma eficaz',
    pt: 'como usar instruções de forma eficaz',
    fr: 'comment utiliser efficacement les consignes',
    it: 'come usare i prompt in modo efficace',
    ru: 'как эффективно использовать промпты',
  },
  'editor.prompt.placeholder': {
    en: 'Write your prompt here or use existing templates',
    es: 'Escribe una indicación o usa una plantilla',
    pt: 'Escreva sua instrução aqui ou use um dos modelos existentes',
    fr: 'Écrivez votre consigne ou utilisez un modèle',
    it: 'Scrivi qui il tuo prompt o usa uno dei modelli disponibili',
    ru: 'Введите промпт или выберите шаблон',
  },
  'editor.prompt.templateFormal': {
    en: 'formal',
    es: 'formal',
    pt: 'formal',
    fr: 'formel',
    it: 'formale',
    ru: 'формальный',
  },
  'editor.prompt.templateFormalValue': {
    en: 'Translate this video in a formal tone of voice. Maintain professional language suitable for a business setting, and avoid informal expressions. Use terminology appropriate to the subject matter. Do not use the following terms: [insert terms].',
    es: 'Traduce este video con un tono formal. Mantén un lenguaje profesional adecuado para un contexto empresarial y evita las expresiones informales. Incluye términos adecuados para un estilo cordial, informativo o entusiasta, según se indique. No uses los siguientes términos: [indica los términos].',
    pt: 'Traduza este vídeo em um tom formal. Mantenha uma linguagem profissional adequada ao ambiente de negócios e evite expressões informais. Inclua termos apropriados a um estilo cordial, informativo ou entusiasmado, conforme indicado. Não use os seguintes termos: [insira os termos].',
    fr: 'Traduisez cette vidéo sur un ton formel. Employez un langage professionnel adapté au monde de l’entreprise et évitez les expressions familières. Utilisez les termes correspondant au style indiqué : cordial, informatif ou enthousiaste. N’utilisez pas les termes suivants : [insérez les termes].',
    it: 'Traduci questo video con un tono formale. Mantieni un linguaggio professionale adatto a un contesto aziendale ed evita le espressioni informali. Usa termini coerenti con lo stile indicato: cordiale, informativo o entusiasta. Non usare i seguenti termini: [inserisci i termini].',
    ru: 'Переведи это видео в формальном тоне. Используй профессиональную лексику, подходящую для делового общения, и избегай разговорных выражений. Используй терминологию, соответствующую тематике видео. Не используй следующие термины: [термины].',
  },
  'editor.prompt.templateIndustry': {
    en: 'industry-specific',
    es: 'específica del sector',
    pt: 'específico do setor',
    fr: 'spécifique au secteur',
    it: 'specifico per il settore',
    ru: 'отраслевой',
  },
  'editor.prompt.templateIndustryValue': {
    en: 'Translate this video using terminology specific to [industry/field]. Refer to our website [website link] for preferred terms and language.',
    es: 'Traduce este video con terminología específica de [sector/campo]. Consulta nuestro sitio web [enlace al sitio web] para conocer la terminología y el estilo que preferimos.',
    pt: 'Traduza este vídeo usando terminologia específica de [setor/área]. Consulte nosso site [link do site] para conhecer os termos e a linguagem que preferimos.',
    fr: 'Traduisez cette vidéo en utilisant la terminologie du secteur ou domaine suivant : [secteur/domaine]. Consultez notre site web [lien vers le site] pour connaître les termes et le langage à privilégier.',
    it: 'Traduci questo video usando la terminologia specifica di [settore/ambito]. Consulta il nostro sito web [link al sito] per conoscere i termini e il linguaggio che preferiamo.',
    ru: 'Переведи это видео с использованием терминологии, характерной для [отрасли/сферы]. Ориентируйся на наш сайт [ссылка на сайт], чтобы использовать предпочтительные термины и стиль.',
  },
  'editor.prompt.templateInformal': {
    en: 'informal',
    es: 'informal',
    pt: 'informal',
    fr: 'informel',
    it: 'informale',
    ru: 'неформальный',
  },
  'editor.prompt.templateInformalValue': {
    en: 'Translate this video in an informal tone of voice. Use conversational language and adapt expressions to make the content approachable and friendly. Avoid using the following terms: [insert terms].',
    es: 'Traduce este video con un tono informal. Usa un lenguaje conversacional para que el contenido resulte cercano y amigable. Adapta las expresiones al estilo indicado: cordial, informativo o entusiasta. Evita los siguientes términos: [indica los términos].',
    pt: 'Traduza este vídeo em um tom informal. Use uma linguagem coloquial para deixar o conteúdo acessível e amigável. Adapte as expressões ao estilo indicado: amigável, informativo ou entusiasmado. Evite os seguintes termos: [insira os termos].',
    fr: 'Traduisez cette vidéo sur un ton informel. Employez un langage conversationnel pour rendre le contenu accessible et convivial. Adaptez les expressions au style indiqué : convivial, informatif ou enthousiaste. Évitez les termes suivants : [insérez les termes].',
    it: 'Traduci questo video con un tono informale. Usa un linguaggio colloquiale per rendere il contenuto accessibile e amichevole. Adatta le espressioni allo stile indicato: amichevole, informativo o entusiasta. Evita i seguenti termini: [inserisci i termini].',
    ru: 'Переведи это видео в неформальном тоне. Используй разговорный язык и адаптируй выражения, чтобы содержание звучало легко и дружелюбно. Не используй следующие термины: [термины].',
  },
  'editor.prompt.templates': {
    en: 'Templates',
    es: 'Plantillas',
    pt: 'Modelos',
    fr: 'Modèles',
    it: 'Modelli',
    ru: 'Шаблоны',
  },
  'editor.project.allProjects': {
    en: 'All projects',
    es: 'Todos los proyectos',
    pt: 'Todos os projetos',
    fr: 'Tous les projets',
    it: 'Tutti i progetti',
    ru: 'Все проекты',
  },
  'editor.project.chooseLocation': {
    en: 'Choose location',
    es: 'Elegir ubicación',
    pt: 'Escolher local',
    fr: 'Choisir un emplacement',
    it: 'Scegli la destinazione',
    ru: 'Выберите место назначения',
  },
  'editor.project.deleteManySubtitle': {
    en: 'You will no longer be able to access the projects.',
    es: 'Ya no podrás acceder a los proyectos.',
    pt: 'Você não poderá mais acessar os projetos.',
    fr: 'Vous ne pourrez plus accéder aux projets.',
    it: 'Non potrai più accedere ai progetti.',
    ru: 'Доступ к проектам будет потерян.',
  },
  'editor.project.deleteManyTitle': {
    en: 'Would you like to permanently delete selected projects?',
    es: '¿Quieres eliminar permanentemente los proyectos seleccionados?',
    pt: 'Deseja excluir permanentemente os projetos selecionados?',
    fr: 'Voulez-vous supprimer définitivement les projets sélectionnés ?',
    it: 'Vuoi eliminare definitivamente i progetti selezionati?',
    ru: 'Удалить выбранные проекты без возможности восстановления?',
  },
  'editor.project.deleteOneSubtitle': {
    en: 'Once deleted, this project will no longer be accessible.',
    es: 'Una vez eliminado, ya no podrás acceder a este proyecto.',
    pt: 'Depois de excluído, este vídeo não estará mais acessível.',
    fr: 'Une fois supprimée, cette vidéo ne sera plus accessible.',
    it: 'Dopo l’eliminazione, il video non sarà più accessibile.',
    ru: 'После удаления проект будет недоступен.',
  },
  'editor.project.deleteOneTitle': {
    en: 'Would you like to permanently delete this project?',
    es: '¿Quieres eliminar este proyecto permanentemente?',
    pt: 'Deseja excluir este projeto permanentemente?',
    fr: 'Voulez-vous supprimer définitivement ce projet ?',
    it: 'Vuoi eliminare definitivamente questo progetto?',
    ru: 'Удалить этот проект без возможности восстановления?',
  },
  'editor.project.fieldRequired': {
    en: 'Field is required',
    es: 'Campo obligatorio',
    pt: 'Campo obrigatório',
    fr: 'Champ obligatoire',
    it: 'Campo obbligatorio',
    ru: 'Обязательное поле',
  },
  'editor.project.move': { en: 'Move', es: 'Mover', pt: 'Mover', fr: 'Déplacer', it: 'Sposta', ru: 'Переместить' },
  'editor.project.moveMany': {
    en: 'Move projects',
    es: 'Mover proyectos',
    pt: 'Mover projetos',
    fr: 'Déplacer les projets',
    it: 'Sposta progetti',
    ru: 'Переместить проекты',
  },
  'editor.project.moveManySubtitle': {
    en: '{count} selected projects will be moved to this folder',
    es: 'Los proyectos seleccionados ({count}) se moverán a esta carpeta',
    pt: 'Os projetos selecionados ({count}) serão movidos para esta pasta',
    fr: '{count} projets sélectionnés seront déplacés vers cet emplacement',
    it: 'I progetti selezionati ({count}) verranno spostati in questa cartella',
    ru: 'Выбранные проекты ({count}) будут перемещены сюда',
  },
  'editor.project.moveOneSubtitle': {
    en: '1 selected project will be moved to this folder',
    es: 'El proyecto seleccionado se moverá a esta carpeta',
    pt: 'O projeto selecionado será movido para esta pasta',
    fr: '1 projet sélectionné sera déplacé vers cet emplacement',
    it: 'Il progetto selezionato verrà spostato in questa cartella',
    ru: '1 выбранный проект будет перемещён сюда',
  },
  'editor.project.name': { en: 'Name', es: 'Nombre', pt: 'Nome', fr: 'Nom', it: 'Nome', ru: 'Название' },
  'editor.project.rename': {
    en: 'Rename project',
    es: 'Cambiar nombre del proyecto',
    pt: 'Renomear projeto',
    fr: 'Renommer le projet',
    it: 'Rinomina progetto',
    ru: 'Переименовать проект',
  },
  'editor.project.selectFolder': {
    en: 'Select folder',
    es: 'Seleccionar carpeta',
    pt: 'Selecionar pasta',
    fr: 'Sélectionner un dossier',
    it: 'Seleziona cartella',
    ru: 'Выберите папку',
  },
  'editor.resizeWarning': {
    en: 'Expand your browser window for best editing experience',
    es: 'Amplía la ventana del navegador para editar mejor',
    pt: 'Amplie a janela do navegador para editar melhor',
    fr: 'Agrandissez la fenêtre du navigateur pour une édition optimale',
    it: 'Allarga la finestra del browser per modificare al meglio',
    ru: 'Расширьте окно браузера для удобной работы в редакторе',
  },
  'editor.search.aria': {
    en: 'Search text',
    es: 'Buscar texto',
    pt: 'Buscar texto',
    fr: 'Rechercher du texte',
    it: 'Cerca testo',
    ru: 'Поиск по тексту',
  },
  'editor.search.placeholder': { en: 'Search', es: 'Buscar', pt: 'Buscar', fr: 'Rechercher', it: 'Cerca', ru: 'Поиск' },
  'editor.segment.add': {
    en: 'Add segment',
    es: 'Añadir segmento',
    pt: 'Adicionar segmento',
    fr: 'Ajouter un segment',
    it: 'Aggiungi segmento',
    ru: 'Добавить сегмент',
  },
  'editor.segment.addSpeaker': {
    en: 'Add Speaker',
    es: 'Añadir hablante',
    pt: 'Adicionar falante',
    fr: 'Ajouter un locuteur',
    it: 'Aggiungi locutore',
    ru: 'Добавить спикера',
  },
  'editor.segment.cancelOrSave': {
    en: 'Cancel or save segment',
    es: 'Cancelar o guardar segmento',
    pt: 'Cancelar ou salvar segmento',
    fr: 'Annuler ou enregistrer le segment',
    it: 'Annulla o salva il segmento',
    ru: 'Отмените изменения или сохраните сегмент',
  },
  'editor.segment.chooseSpeaker': {
    en: 'Choose speaker',
    es: 'Elegir hablante',
    pt: 'Escolher falante',
    fr: 'Choisir un locuteur',
    it: 'Scegli locutore',
    ru: 'Выберите спикера',
  },
  'editor.segment.createError': {
    en: 'We could not create a segment. Try again',
    es: 'No se pudo crear el segmento. Inténtalo de nuevo',
    pt: 'Não foi possível criar o segmento. Tente novamente',
    fr: 'Impossible de créer le segment. Réessayez',
    it: 'Impossibile creare il segmento. Riprova',
    ru: 'Не удалось создать сегмент. Попробуйте снова',
  },
  'editor.segment.deleteAllTitle': {
    en: 'Would you like to permanently delete all empty segments?',
    es: '¿Quieres eliminar permanentemente todos los segmentos vacíos?',
    pt: 'Deseja excluir permanentemente todos os segmentos vazios?',
    fr: 'Voulez-vous supprimer définitivement tous les segments vides ?',
    it: 'Vuoi eliminare definitivamente tutti i segmenti vuoti?',
    ru: 'Удалить все пустые сегменты без возможности восстановления?',
  },
  'editor.segment.deleteTitle': {
    en: 'Would you like to delete this segment?',
    es: '¿Quieres eliminar este segmento?',
    pt: 'Deseja excluir este segmento?',
    fr: 'Voulez-vous supprimer ce segment ?',
    it: 'Vuoi eliminare questo segmento?',
    ru: 'Удалить этот сегмент?',
  },
  'editor.segment.enterText': {
    en: 'Enter text',
    es: 'Introduce texto',
    pt: 'Digite o texto',
    fr: 'Saisissez le texte',
    it: 'Inserisci testo',
    ru: 'Введите текст',
  },
  'editor.segment.merge': {
    en: 'Merge and re-translate',
    es: 'Combinar y volver a traducir',
    pt: 'Mesclar e retraduzir',
    fr: 'Fusionner et retraduire',
    it: 'Unisci e ritraduci',
    ru: 'Объединить и перевести заново',
  },
  'editor.segment.mergeOnly': {
    en: 'Merge',
    es: 'Combinar',
    pt: 'Mesclar',
    fr: 'Fusionner',
    it: 'Unisci',
    ru: 'Объединить',
  },
  'editor.segment.mergeError': {
    en: "We couldn't merge these segments. Please try again",
    es: 'No se pudieron combinar estos segmentos. Inténtalo de nuevo',
    pt: 'Não foi possível mesclar os segmentos. Tente novamente',
    fr: 'Impossible de fusionner ces segments. Réessayez',
    it: 'Impossibile unire i segmenti. Riprova',
    ru: 'Не удалось объединить сегменты. Попробуйте снова',
  },
  'editor.segment.mergingProgress': {
    en: 'Merging segments...',
    es: 'Combinando segmentos...',
    pt: 'Mesclando segmentos...',
    fr: 'Fusion des segments...',
    it: 'Unione dei segmenti...',
    ru: 'Объединяем сегменты...',
  },
  'editor.segment.saveError': {
    en: "We couldn't save the segment. Please try again",
    es: 'No se pudo guardar el segmento. Inténtalo de nuevo',
    pt: 'Não foi possível salvar o segmento. Tente novamente',
    fr: 'Impossible d’enregistrer le segment. Réessayez',
    it: 'Impossibile salvare il segmento. Riprova',
    ru: 'Не удалось сохранить сегмент. Попробуйте снова',
  },
  'editor.segment.saveManyError': {
    en: "We couldn't save these segments. Please try again",
    es: 'No se pudieron guardar estos segmentos. Inténtalo de nuevo',
    pt: 'Não foi possível salvar os segmentos. Tente novamente',
    fr: 'Impossible d’enregistrer ces segments. Réessayez',
    it: 'Impossibile salvare i segmenti. Riprova',
    ru: 'Не удалось сохранить сегменты. Попробуйте снова',
  },
  'editor.segment.splitDescriptionCurrent': {
    en: 'If you want to keep the current translation, you can split the translated text.',
    es: 'Si quieres conservar la traducción actual, puedes dividir el texto traducido.',
    pt: 'Para manter a tradução atual, divida o texto traduzido.',
    fr: 'Pour conserver la traduction actuelle, vous pouvez scinder le texte traduit.',
    it: 'Per mantenere la traduzione attuale, puoi dividere il testo tradotto.',
    ru: 'Чтобы сохранить текущий перевод, разделите переведённый текст.',
  },
  'editor.segment.splitDescriptionOriginal': {
    en: 'Splitting the original text will result in the re-translation of relevant segments.',
    es: 'Al dividir el texto original, los segmentos correspondientes se volverán a traducir.',
    pt: 'Dividir o texto original fará com que os segmentos correspondentes sejam retraduzidos.',
    fr: 'La division du texte d’origine entraînera la retraduction des segments concernés.',
    it: 'Dividendo il testo originale, i segmenti interessati verranno ritradotti.',
    ru: 'После разделения исходного текста соответствующие сегменты будут переведены заново.',
  },
  'editor.segment.splitDontShow': {
    en: "Don't show this again",
    es: 'No volver a mostrar',
    pt: 'Não mostrar novamente',
    fr: 'Ne plus afficher',
    it: 'Non mostrare più',
    ru: 'Больше не показывать',
  },
  'editor.segment.splitSubmit': {
    en: 'Split segments',
    es: 'Dividir segmentos',
    pt: 'Dividir segmentos',
    fr: 'Scinder les segments',
    it: 'Dividi i segmenti',
    ru: 'Разделить сегменты',
  },
  'editor.segment.splitTitle': {
    en: 'New segments will be re-translated',
    es: 'Los nuevos segmentos se volverán a traducir',
    pt: 'Os novos segmentos serão retraduzidos',
    fr: 'Les nouveaux segments seront retraduits',
    it: 'I nuovi segmenti verranno ritradotti',
    ru: 'Новые сегменты будут переведены заново',
  },
  'editor.segment.translationPlaceholder': {
    en: 'Your translation will appear here',
    es: 'Aquí aparecerá la traducción',
    pt: 'A tradução aparecerá aqui',
    fr: 'La traduction apparaîtra ici',
    it: 'La traduzione apparirà qui',
    ru: 'Здесь появится перевод',
  },
  'editor.segment.voiceOver': {
    en: 'Voice over',
    es: 'Voz en off',
    pt: 'Narração',
    fr: 'Voix off',
    it: 'Voce fuori campo',
    ru: 'Озвучка',
  },
  'editor.segmentPreview.generating': {
    en: 'Generating preview...',
    es: 'Generando vista previa...',
    pt: 'Gerando prévia...',
    fr: 'Génération de l’aperçu…',
    it: 'Generazione anteprima...',
    ru: 'Создаём предпросмотр…',
  },
  'editor.segmentPreview.generatingAria': {
    en: 'Generating segment audio preview',
    es: 'Generando la vista previa de audio del segmento',
    pt: 'Gerando prévia de áudio do segmento',
    fr: 'Génération de l’aperçu audio du segment',
    it: 'Generazione dell’anteprima audio del segmento',
    ru: 'Создаётся предпросмотр аудио сегмента',
  },
  'editor.segmentPreview.generatingAudio': {
    en: 'Generating audio...',
    es: 'Generando audio...',
    pt: 'Gerando áudio...',
    fr: 'Génération de l’audio…',
    it: 'Generazione audio...',
    ru: 'Создаём аудио…',
  },
  'editor.segmentPreview.processingSeconds': {
    en: '{label} ({seconds}s)',
    es: '{label} ({seconds} s)',
    pt: '{label} ({seconds} s)',
    fr: '{label} ({seconds} s)',
    it: '{label} ({seconds} s)',
    ru: '{label} ({seconds} с)',
  },
  'editor.segmentPreview.stillProcessing': {
    en: 'Still processing...',
    es: 'Aún procesando...',
    pt: 'Ainda processando...',
    fr: 'Traitement toujours en cours…',
    it: 'Elaborazione ancora in corso...',
    ru: 'Всё ещё обрабатываем…',
  },
  'editor.segmentPreview.stop': {
    en: 'Stop preview',
    es: 'Detener vista previa',
    pt: 'Parar prévia',
    fr: 'Arrêter l’aperçu',
    it: 'Interrompi l’anteprima',
    ru: 'Остановить воспроизведение',
  },
  'editor.segmentPreview.stopAria': {
    en: 'Stop segment audio preview',
    es: 'Detener la vista previa de audio del segmento',
    pt: 'Parar prévia de áudio do segmento',
    fr: 'Arrêter l’aperçu audio du segment',
    it: 'Interrompi l’anteprima audio del segmento',
    ru: 'Остановить воспроизведение аудио сегмента',
  },
  'editor.segmentPreview.preview': {
    en: 'Preview segment audio',
    es: 'Reproducir el audio del fragmento',
    pt: 'Prévia do áudio do segmento',
    fr: 'Écouter l’aperçu audio du segment',
    it: 'Anteprima audio del segmento',
    ru: 'Прослушать аудио сегмента',
  },
  'editor.segmentPreview.retry': {
    en: 'Generation timed out. Click to retry.',
    es: 'Se agotó el tiempo de generación. Haz clic para reintentar.',
    pt: 'O tempo de geração expirou. Clique para tentar novamente.',
    fr: 'Le délai de génération a expiré. Cliquez pour réessayer.',
    it: 'Tempo di generazione scaduto. Fai clic per riprovare.',
    ru: 'Не удалось создать аудио вовремя. Нажмите, чтобы повторить.',
  },
  'editor.segmentPreview.retryAria': {
    en: 'Retry segment audio preview',
    es: 'Reintentar la vista previa de audio del segmento',
    pt: 'Tentar novamente a prévia de áudio do segmento',
    fr: 'Réessayer l’aperçu audio du segment',
    it: 'Riprova l’anteprima audio del segmento',
    ru: 'Повторить предпросмотр аудио сегмента',
  },
  'editor.segmentPreview.translateFirst': {
    en: 'Translate segment before previewing audio',
    es: 'Traduce el segmento antes de escuchar el audio',
    pt: 'Traduza o segmento antes de ouvir a prévia',
    fr: 'Traduisez le segment avant d’écouter l’aperçu audio',
    it: 'Traduci il segmento prima di ascoltare l’anteprima audio',
    ru: 'Переведите сегмент перед прослушиванием аудио',
  },
  'editor.segmentPreview.translating': {
    en: 'Translating segment...',
    es: 'Traduciendo segmento...',
    pt: 'Traduzindo segmento...',
    fr: 'Traduction du segment…',
    it: 'Traduzione del segmento...',
    ru: 'Переводим сегмент…',
  },
  'editor.translationVariants.alternativesFew': {
    en: '{count} translation alternatives',
    es: '{count} alternativas de traducción',
    pt: '{count} alternativas de tradução',
    fr: '{count} variantes de traduction',
    it: '{count} alternative di traduzione',
    ru: '{count} варианта перевода',
  },
  'editor.translationVariants.alternativesMany': {
    en: '{count} translation alternatives',
    es: '{count} alternativas de traducción',
    pt: '{count} alternativas de tradução',
    fr: '{count} variantes de traduction',
    it: '{count} alternative di traduzione',
    ru: '{count} вариантов перевода',
  },
  'editor.translationVariants.alternativesOne': {
    en: '{count} translation alternative',
    es: '{count} alternativa de traducción',
    pt: '{count} alternativa de tradução',
    fr: '{count} variante de traduction',
    it: '{count} alternativa di traduzione',
    ru: '{count} вариант перевода',
  },
  'editor.translationVariants.alternativesOther': {
    en: '{count} translation alternatives',
    es: '{count} alternativas de traducción',
    pt: '{count} alternativas de tradução',
    fr: '{count} variantes de traduction',
    it: '{count} alternative di traduzione',
    ru: '{count} варианта перевода',
  },
  'editor.translationVariants.faster': {
    en: 'Faster',
    es: 'Más rápido',
    pt: 'Mais rápido',
    fr: 'Plus rapide',
    it: 'Più veloce',
    ru: 'Быстрее',
  },
  'editor.translationVariants.info': {
    en: 'Preview the selected translation using the row play button or the main player.',
    es: 'Escucha la traducción seleccionada con el botón de reproducción de la fila o el reproductor principal.',
    pt: 'Ouça a tradução selecionada com o botão de reprodução da linha ou no player principal.',
    fr: 'Écoutez la traduction sélectionnée avec le bouton de lecture de la ligne ou le lecteur principal.',
    it: 'Ascolta la traduzione selezionata con il pulsante di riproduzione della riga o il player principale.',
    ru: 'Прослушайте выбранный перевод кнопкой воспроизведения в строке или в основном плеере.',
  },
  'editor.translationVariants.original': {
    en: 'Original',
    es: 'Original',
    pt: 'Original',
    fr: 'Original',
    it: 'Originale',
    ru: 'Исходная',
  },
  'editor.translationVariants.recommended': {
    en: 'Recommended',
    es: 'Recomendada',
    pt: 'Recomendada',
    fr: 'Recommandée',
    it: 'Consigliata',
    ru: 'Рекомендуем',
  },
  'editor.translationVariants.slower': {
    en: 'Slower',
    es: 'Más lento',
    pt: 'Mais lento',
    fr: 'Plus lente',
    it: 'Più lenta',
    ru: 'Медленнее',
  },
  'editor.speaker.selectSubtitle': {
    en: 'The segments being merged have different speakers. Select a speaker for the new segment.',
    es: 'Los segmentos que se van a combinar tienen hablantes distintos. Elige el hablante del nuevo segmento.',
    pt: 'Os segmentos que serão mesclados têm falantes diferentes. Escolha o falante do novo segmento.',
    fr: 'Les segments à fusionner ont des locuteurs différents. Choisissez le locuteur du nouveau segment.',
    it: 'I segmenti da unire hanno locutori diversi. Scegli il locutore del nuovo segmento.',
    ru: 'В объединяемых сегментах разные спикеры. Выберите спикера для нового сегмента.',
  },
  'editor.speaker.speaker': {
    en: 'Speaker',
    es: 'Hablante',
    pt: 'Falante',
    fr: 'Locuteur',
    it: 'Locutore',
    ru: 'Спикер',
  },
  'editor.speaker.selectTitle': {
    en: 'Select new segment speaker',
    es: 'Seleccionar hablante del nuevo segmento',
    pt: 'Selecionar falante do novo segmento',
    fr: 'Choisir le locuteur du nouveau segment',
    it: 'Seleziona il locutore del nuovo segmento',
    ru: 'Выберите спикера нового сегмента',
  },
  'editor.srt.identicalTimestamps': {
    en: 'Please ensure both SRT files have identical timestamps or remove one.',
    es: 'Asegúrate de que ambos archivos SRT tengan las mismas marcas de tiempo o elimina uno.',
    pt: 'Verifique se os dois arquivos SRT têm as mesmas marcações de tempo ou remova um deles.',
    fr: 'Vérifiez que les deux fichiers SRT ont les mêmes horodatages ou supprimez-en un.',
    it: 'Assicurati che i due file SRT abbiano gli stessi timestamp oppure rimuovine uno.',
    ru: 'Убедитесь, что таймкоды в обоих файлах SRT совпадают, или удалите один из файлов.',
  },
  'editor.srt.applyTitle': {
    en: 'Do you want to apply changes?',
    es: '¿Quieres aplicar los cambios?',
    pt: 'Deseja aplicar as alterações?',
    fr: 'Voulez-vous appliquer les modifications ?',
    it: 'Vuoi applicare le modifiche?',
    ru: 'Применить изменения?',
  },
  'editor.srt.applySubtitle': {
    en: 'The SRT file in the project was modified. This will make permanent changes to the project.',
    es: 'El archivo SRT del proyecto se ha modificado. Esto cambiará el proyecto permanentemente.',
    pt: 'O arquivo SRT do projeto foi modificado. Isso fará alterações permanentes no projeto.',
    fr: 'Le fichier SRT du projet a été modifié. Cela modifiera définitivement le projet.',
    it: 'Il file SRT del progetto è stato modificato. Le modifiche saranno permanenti.',
    ru: 'Файл SRT в проекте изменён. Изменения в проекте будут необратимыми.',
  },
  'editor.srt.fillDetails': {
    en: 'Fill in your project details and upload the file',
    es: 'Completa los detalles del proyecto y sube el archivo',
    pt: 'Preencha os detalhes do projeto e envie o arquivo',
    fr: 'Renseignez les détails du projet et importez le fichier',
    it: 'Compila i dettagli del progetto e carica il file',
    ru: 'Укажите параметры проекта и загрузите файл',
  },
  'editor.srt.howTo': {
    en: 'about how to upload an SRT file correctly',
    es: 'sobre cómo subir correctamente un archivo SRT',
    pt: 'sobre como enviar corretamente um arquivo SRT',
    fr: 'sur la façon d’importer correctement un fichier SRT',
    it: 'su come caricare correttamente un file SRT',
    ru: 'о том, как правильно загрузить файл SRT',
  },
  'editor.srt.languageInVideo': {
    en: 'Language in video',
    es: 'Idioma del video',
    pt: 'Idioma do vídeo',
    fr: 'Langue de la vidéo',
    it: 'Lingua del video',
    ru: 'Язык видео',
  },
  'editor.srt.upload': {
    en: 'Upload SRT',
    es: 'Subir SRT',
    pt: 'Enviar SRT',
    fr: 'Importer le SRT',
    it: 'Carica SRT',
    ru: 'Загрузить SRT',
  },
  'editor.srt.wrongFormat': {
    en: 'Wrong SRT file format. Use another file.',
    es: 'Formato de archivo SRT incorrecto. Usa otro archivo.',
    pt: 'Formato de arquivo SRT incorreto. Use outro arquivo.',
    fr: 'Format de fichier SRT incorrect. Utilisez un autre fichier.',
    it: 'Formato SRT non valido. Usa un altro file.',
    ru: 'Неверный формат файла SRT. Выберите другой файл.',
  },
  'editor.srtWarning.gotIt': {
    en: 'Got it!',
    es: '¡Entendido!',
    pt: 'Entendi!',
    fr: 'Compris !',
    it: 'Ho capito!',
    ru: 'Понятно!',
  },
  'editor.srtWarning.speakers': {
    en: 'We assigned speakers automatically. Please check them before dubbing.',
    es: 'Hemos asignado los hablantes automáticamente. Revísalos antes de iniciar el doblaje',
    pt: 'Atribuímos os falantes automaticamente. Verifique antes de iniciar a dublagem',
    fr: 'Nous avons attribué les locuteurs automatiquement. Vérifiez-les avant de lancer le doublage',
    it: 'Abbiamo assegnato automaticamente i locutori. Verificali prima di avviare il doppiaggio.',
    ru: 'Мы автоматически назначили спикеров. Проверьте их перед озвучкой',
  },
  'editor.srtWarning.timestamps': {
    en: 'We have merged some of your timestamps for better dubbing quality.',
    es: 'Hemos combinado algunas marcas de tiempo para mejorar la calidad del doblaje',
    pt: 'Combinamos algumas das suas marcações de tempo para melhorar a qualidade da dublagem',
    fr: 'Nous avons fusionné certains horodatages pour améliorer la qualité du doublage',
    it: 'Abbiamo unito alcuni timestamp per migliorare la qualità del doppiaggio',
    ru: 'Мы объединили некоторые таймкоды, чтобы улучшить качество озвучки',
  },
  'editor.srtWarning.title': {
    en: 'Before dubbing, please review the following',
    es: 'Antes de doblar, revisa lo siguiente',
    pt: 'Antes de dublar, revise os seguintes pontos',
    fr: 'Avant le doublage, veuillez prendre connaissance des points suivants',
    it: 'Prima del doppiaggio, controlla quanto segue',
    ru: 'Перед озвучкой обратите внимание',
  },
  'editor.timestamp.exceedsVideo': {
    en: 'The timestamp exceeds the video length. Please enter a valid timestamp.',
    es: 'La marca de tiempo supera la duración del video. Introduce una válida',
    pt: 'A marca de tempo ultrapassa a duração do vídeo. Insira uma marca de tempo válida.',
    fr: 'L’horodatage dépasse la durée de la vidéo. Saisissez une valeur valide.',
    it: 'Il timestamp supera la durata del video. Inseriscine uno valido',
    ru: 'Таймкод выходит за пределы видео. Введите корректное значение.',
  },
  'editor.timestamp.overlap': {
    en: 'This segment overlaps with another segment',
    es: 'Hay una superposición aquí',
    pt: 'Há uma sobreposição aqui',
    fr: 'Il y a un chevauchement ici',
    it: 'Qui c’è una sovrapposizione',
    ru: 'Здесь есть наложение',
  },
  'editor.timestamp.startAfterEnd': {
    en: 'The start timestamp cannot exceed the end timestamp',
    es: 'La marca inicial no puede ser posterior a la final',
    pt: 'A marca inicial não pode ser posterior à final',
    fr: 'L’horodatage de début ne peut pas dépasser celui de fin',
    it: 'Il timestamp iniziale non può superare quello finale',
    ru: 'Начальный таймкод не может быть позже конечного',
  },
  'editor.versions.autoAfterMerge': {
    en: 'Auto (after merge)',
    es: 'Automático (después de combinar)',
    pt: 'Automático (após mesclar)',
    fr: 'Auto (après fusion)',
    it: 'Automatico (dopo l’unione)',
    ru: 'Авто (после объединения)',
  },
  'editor.versions.autoAfterTranslation': {
    en: 'Auto (after translation)',
    es: 'Automático (después de traducir)',
    pt: 'Automático (após a tradução)',
    fr: 'Auto (après traduction)',
    it: 'Automatico (dopo la traduzione)',
    ru: 'Авто (после перевода)',
  },
  'editor.versions.autoSave': {
    en: 'Auto save',
    es: 'Guardado automático',
    pt: 'Salvamento automático',
    fr: 'Enregistrement automatique',
    it: 'Salvataggio automatico',
    ru: 'Автосохранение',
  },
  'editor.versions.changed': {
    en: 'Changed',
    es: 'Modificado',
    pt: 'Alterado',
    fr: 'Modifié',
    it: 'Modificato',
    ru: 'Изменён',
  },
  'editor.versions.changes': {
    en: 'Changes',
    es: 'Cambios',
    pt: 'Alterações',
    fr: 'Modifications',
    it: 'Modifiche',
    ru: 'Изменения',
  },
  'editor.versions.closeAria': {
    en: 'Close version history',
    es: 'Cerrar historial de versiones',
    pt: 'Fechar histórico de versões',
    fr: 'Fermer l’historique des versions',
    it: 'Chiudi cronologia versioni',
    ru: 'Закрыть историю версий',
  },
  'editor.versions.compareError': {
    en: 'Could not compare with Version {version}',
    es: 'No se pudo comparar con la versión {version}',
    pt: 'Não foi possível comparar com a versão {version}',
    fr: 'Impossible de comparer avec la version {version}',
    it: 'Impossibile confrontare con la versione {version}',
    ru: 'Не удалось сравнить с версией {version}',
  },
  'editor.versions.compareLoading': {
    en: 'Loading comparison with Version {version}...',
    es: 'Cargando comparación con la versión {version}...',
    pt: 'Carregando comparação com a versão {version}...',
    fr: 'Chargement de la comparaison avec la version {version}…',
    it: 'Caricamento del confronto con la versione {version}...',
    ru: 'Загружаем сравнение с версией {version}…',
  },
  'editor.versions.compareSummary': {
    en: 'Compared with Version {version}: {added} new, {changed} changed, {removed} removed',
    es: 'Comparación con la versión {version} • Nuevos: {added} • Modificados: {changed} • Eliminados: {removed}',
    pt: 'Comparação com a versão {version} • Novos: {added} • Alterados: {changed} • Removidos: {removed}',
    fr: 'Comparaison avec la version {version} • Ajouts : {added} • Modifications : {changed} • Suppressions : {removed}',
    it: 'Confronto con la versione {version} • Aggiunte: {added} • Modifiche: {changed} • Rimozioni: {removed}',
    ru: 'Сравнение с версией {version}: новых — {added}, изменённых — {changed}, удалённых — {removed}',
  },
  'editor.versions.current': { en: 'Current', es: 'Actual', pt: 'Atual', fr: 'Actuelle', it: 'Attuale', ru: 'Текущая' },
  'editor.versions.customVoice': {
    en: 'Custom voice',
    es: 'Voz personalizada',
    pt: 'Voz personalizada',
    fr: 'Voix personnalisée',
    it: 'Voce personalizzata',
    ru: 'Пользовательский голос',
  },
  'editor.versions.detailError': {
    en: 'Version details are unavailable',
    es: 'Los detalles de la versión no están disponibles',
    pt: 'Os detalhes da versão não estão disponíveis',
    fr: 'Les détails de la version sont indisponibles',
    it: 'I dettagli della versione non sono disponibili',
    ru: 'Сведения о версии недоступны',
  },
  'editor.versions.detailLoadError': {
    en: 'We could not load the selected version.',
    es: 'No se pudo cargar la versión seleccionada.',
    pt: 'Não foi possível carregar a versão selecionada.',
    fr: 'Impossible de charger la version sélectionnée.',
    it: 'Impossibile caricare la versione selezionata.',
    ru: 'Не удалось загрузить выбранную версию.',
  },
  'editor.versions.full': {
    en: 'Full version',
    es: 'Versión completa',
    pt: 'Versão completa',
    fr: 'Version complète',
    it: 'Versione completa',
    ru: 'Полная версия',
  },
  'editor.versions.history': {
    en: 'Version history',
    es: 'Historial de versiones',
    pt: 'Histórico de versões',
    fr: 'Historique des versions',
    it: 'Cronologia versioni',
    ru: 'История версий',
  },
  'editor.versions.initialSummary': {
    en: 'Initial snapshot • Segments: {count}',
    es: 'Instantánea inicial • Segmentos: {count}',
    pt: 'Captura inicial • Segmentos: {count}',
    fr: 'Instantané initial • Segments : {count}',
    it: 'Snapshot iniziale • Segmenti: {count}',
    ru: 'Исходная версия • Сегменты: {count}',
  },
  'editor.versions.manualSave': {
    en: 'Manual save',
    es: 'Guardado manual',
    pt: 'Salvamento manual',
    fr: 'Enregistrement manuel',
    it: 'Salvataggio manuale',
    ru: 'Сохранено вручную',
  },
  'editor.versions.name': {
    en: 'Version name',
    es: 'Nombre de la versión',
    pt: 'Nome da versão',
    fr: 'Nom de la version',
    it: 'Nome versione',
    ru: 'Название версии',
  },
  'editor.versions.new': { en: 'New', es: 'Nuevo', pt: 'Novo', fr: 'Nouveau', it: 'Nuovo', ru: 'Новый' },
  'editor.versions.noChanges': {
    en: 'No segment text or timing changes in this version.',
    es: 'En esta versión no hay cambios en el texto ni en los tiempos de los segmentos.',
    pt: 'Não há alterações no texto ou nos tempos dos segmentos nesta versão.',
    fr: 'Cette version ne comporte aucune modification du texte ou des horodatages des segments.',
    it: 'In questa versione non ci sono modifiche al testo o ai tempi dei segmenti.',
    ru: 'В этой версии нет изменений текста или таймкодов сегментов.',
  },
  'editor.versions.noText': {
    en: 'No text',
    es: 'Sin texto',
    pt: 'Sem texto',
    fr: 'Aucun texte',
    it: 'Nessun testo',
    ru: 'Нет текста',
  },
  'editor.versions.noVoice': {
    en: 'No voice',
    es: 'Sin voz',
    pt: 'Sem voz',
    fr: 'Aucune voix',
    it: 'Nessuna voce',
    ru: 'Нет голоса',
  },
  'editor.versions.openAria': {
    en: 'Open transcript version history',
    es: 'Abrir el historial de versiones de la transcripción',
    pt: 'Abrir histórico de versões da transcrição',
    fr: 'Ouvrir l’historique des versions de la transcription',
    it: 'Apri la cronologia delle versioni della trascrizione',
    ru: 'Открыть историю версий транскрипции',
  },
  'editor.versions.openVersionAria': {
    en: 'Open version {version}',
    es: 'Abrir versión {version}',
    pt: 'Abrir versão {version}',
    fr: 'Ouvrir la version {version}',
    it: 'Apri versione {version}',
    ru: 'Открыть версию {version}',
  },
  'editor.versions.optional': {
    en: 'Optional',
    es: 'Opcional',
    pt: 'Opcional',
    fr: 'Facultatif',
    it: 'Facoltativo',
    ru: 'Необязательно',
  },
  'editor.versions.placeholder': {
    en: 'Saved translation versions will appear here',
    es: 'Las versiones guardadas de la traducción aparecerán aquí',
    pt: 'As versões salvas da tradução aparecerão aqui',
    fr: 'Les versions enregistrées de la traduction apparaîtront ici',
    it: 'Le versioni salvate della traduzione appariranno qui',
    ru: 'Здесь появятся сохранённые версии перевода',
  },
  'editor.versions.previousUnavailable': {
    en: 'Previous version is unavailable',
    es: 'La versión anterior no está disponible',
    pt: 'A versão anterior não está disponível',
    fr: 'La version précédente est indisponible',
    it: 'La versione precedente non è disponibile',
    ru: 'Предыдущая версия недоступна',
  },
  'editor.versions.removed': {
    en: 'Removed',
    es: 'Eliminado',
    pt: 'Removido',
    fr: 'Supprimé',
    it: 'Rimosso',
    ru: 'Удалён',
  },
  'editor.versions.retry': {
    en: 'Retry',
    es: 'Reintentar',
    pt: 'Tentar novamente',
    fr: 'Réessayer',
    it: 'Riprova',
    ru: 'Повторить',
  },
  'editor.versions.rollback': {
    en: 'Rollback',
    es: 'Restaurar',
    pt: 'Restaurar',
    fr: 'Restaurer',
    it: 'Ripristina',
    ru: 'Откатить',
  },
  'editor.versions.rollbackAria': {
    en: 'Rollback to version {version}',
    es: 'Restaurar la versión {version}',
    pt: 'Restaurar para a versão {version}',
    fr: 'Restaurer la version {version}',
    it: 'Ripristina la versione {version}',
    ru: 'Откатить до версии {version}',
  },
  'editor.versions.rollbackDescription': {
    en: 'This will restore all segments to the state saved in version {version}. Your current state will be auto-saved as a new version before rollback.',
    es: 'Todos los segmentos volverán al estado guardado en la versión {version}. El estado actual se guardará automáticamente como una nueva versión antes de restaurar.',
    pt: 'Todos os segmentos serão restaurados ao estado salvo na versão {version}. O estado atual será salvo automaticamente como uma nova versão antes da restauração.',
    fr: 'Tous les segments seront restaurés dans l’état enregistré dans la version {version}. L’état actuel sera automatiquement enregistré dans une nouvelle version avant la restauration.',
    it: 'Tutti i segmenti verranno ripristinati allo stato salvato nella versione {version}. Prima del ripristino, lo stato attuale verrà salvato automaticamente come nuova versione.',
    ru: 'Все сегменты вернутся к состоянию, сохранённому в версии {version}. Перед откатом текущее состояние автоматически сохранится как новая версия.',
  },
  'editor.versions.rollbackError': {
    en: 'Could not rollback transcript version. Please try again.',
    es: 'No se pudo restaurar la versión de la transcripción. Inténtalo de nuevo.',
    pt: 'Não foi possível restaurar a versão da transcrição. Tente novamente.',
    fr: 'Impossible de restaurer la version de la transcription. Réessayez.',
    it: 'Impossibile ripristinare la versione della trascrizione. Riprova.',
    ru: 'Не удалось откатить версию транскрипции. Попробуйте снова.',
  },
  'editor.versions.rollbackSuccess': {
    en: 'Transcript restored from the selected version',
    es: 'Transcripción restaurada desde la versión',
    pt: 'Transcrição restaurada da versão',
    fr: 'Transcription restaurée depuis la version',
    it: 'Trascrizione ripristinata dalla versione',
    ru: 'Транскрипция восстановлена из версии',
  },
  'editor.versions.rollbackTitle': {
    en: 'Rollback to version {version}?',
    es: '¿Restaurar la versión {version}?',
    pt: 'Restaurar para a versão {version}?',
    fr: 'Restaurer la version {version} ?',
    it: 'Ripristinare la versione {version}?',
    ru: 'Откатить до версии {version}?',
  },
  'editor.versions.save': {
    en: 'Save transcript version',
    es: 'Guardar versión',
    pt: 'Salvar versão',
    fr: 'Enregistrer la version',
    it: 'Salva versione',
    ru: 'Сохранить версию',
  },
  'editor.versions.saveDescription': {
    en: 'Create a restore point for the current transcript state.',
    es: 'Crea un punto de restauración para el estado actual de la transcripción.',
    pt: 'Crie um ponto de restauração para o estado atual da transcrição.',
    fr: 'Créez une version pour l’état actuel de la transcription.',
    it: 'Crea un punto di ripristino per lo stato attuale della trascrizione.',
    ru: 'Создайте точку восстановления для текущего состояния транскрипции.',
  },
  'editor.versions.saveError': {
    en: 'Could not save transcript version. Please try again.',
    es: 'No se pudo guardar la versión de la transcripción. Inténtalo de nuevo.',
    pt: 'Não foi possível salvar a versão da transcrição. Tente novamente.',
    fr: 'Impossible d’enregistrer la version de la transcription. Réessayez.',
    it: 'Impossibile salvare la versione della trascrizione. Riprova.',
    ru: 'Не удалось сохранить версию транскрипции. Попробуйте снова.',
  },
  'editor.versions.saved': {
    en: 'Transcript version saved',
    es: 'Versión de la transcripción guardada',
    pt: 'Versão da transcrição salva',
    fr: 'Version de la transcription enregistrée',
    it: 'Versione della trascrizione salvata',
    ru: 'Версия транскрипции сохранена',
  },
  'editor.versions.saving': {
    en: 'Saving...',
    es: 'Guardando...',
    pt: 'Salvando...',
    fr: 'Enregistrement…',
    it: 'Salvataggio...',
    ru: 'Сохраняем…',
  },
  'editor.versions.segment': {
    en: 'Segment {number}',
    es: 'Segmento {number}',
    pt: 'Segmento {number}',
    fr: 'Segment {number}',
    it: 'Segmento {number}',
    ru: 'Сегмент {number}',
  },
  'editor.versions.segments': {
    en: 'Segments: {count}',
    es: 'Segmentos: {count}',
    pt: 'Segmentos: {count}',
    fr: 'Segments : {count}',
    it: 'Segmenti: {count}',
    ru: 'Сегменты: {count}',
  },
  'editor.versions.select': {
    en: 'Select a version to preview its saved segments.',
    es: 'Selecciona una versión para ver los segmentos guardados.',
    pt: 'Selecione uma versão para visualizar os segmentos salvos.',
    fr: 'Sélectionnez une version pour afficher ses segments enregistrés.',
    it: 'Seleziona una versione per visualizzare i segmenti salvati.',
    ru: 'Выберите версию, чтобы посмотреть сохранённые сегменты.',
  },
  'editor.versions.showFull': {
    en: 'Show full version',
    es: 'Mostrar versión completa',
    pt: 'Mostrar versão completa',
    fr: 'Afficher la version complète',
    it: 'Mostra versione completa',
    ru: 'Показать полную версию',
  },
  'editor.versions.speakerVoices': {
    en: 'Speaker voices',
    es: 'Voces de los hablantes',
    pt: 'Vozes dos falantes',
    fr: 'Voix des locuteurs',
    it: 'Voci dei locutori',
    ru: 'Голоса спикеров',
  },
  'editor.versions.timing': {
    en: 'Timing',
    es: 'Tiempos',
    pt: 'Tempos',
    fr: 'Horodatage',
    it: 'Tempistica',
    ru: 'Таймкоды',
  },
  'editor.versions.version': {
    en: 'Version {version}',
    es: 'Versión {version}',
    pt: 'Versão {version}',
    fr: 'Version {version}',
    it: 'Versione {version}',
    ru: 'Версия {version}',
  },
  'editor.toast.voiceMissingDesktop': {
    en: 'You can select another speaker’s voice in the desktop version.',
    es: 'Puedes seleccionar otra voz para el hablante en la versión de escritorio.',
    pt: 'Você pode escolher outra voz para o locutor na versão para computador.',
    fr: 'Vous pouvez choisir une autre voix pour le locuteur dans la version pour ordinateur.',
    it: 'Puoi scegliere un’altra voce per il locutore nella versione desktop.',
    ru: 'Другой голос для спикера можно выбрать в версии для компьютера.',
  },
  'editor.toast.voiceMissingTitle': {
    en: "We couldn't find a voice similar to the speaker’s voice in your video.",
    es: 'No encontramos una voz similar para el hablante de tu video.',
    pt: 'Não encontramos uma voz semelhante à do locutor do seu vídeo.',
    fr: 'Nous n’avons pas trouvé de voix similaire pour le locuteur de votre vidéo.',
    it: 'Non abbiamo trovato una voce simile per il locutore del video.',
    ru: 'Мы не нашли похожий голос для спикера в вашем видео.',
  },
  'editor.toast.voiceMissingWeb': {
    en: "You can select another speaker's voice by clicking the speaker's voice button.",
    es: 'Puedes seleccionar otra voz pulsando el botón de voz del hablante.',
    pt: 'Você pode escolher outra voz clicando no botão de voz do locutor.',
    fr: 'Vous pouvez choisir une autre voix en cliquant sur le bouton correspondant à la voix du locuteur.',
    it: 'Puoi scegliere un’altra voce facendo clic sul pulsante della voce del locutore.',
    ru: 'Другой голос можно выбрать, нажав кнопку голоса спикера.',
  },
  'editor.video.applyLastChanges': {
    en: 'Lip-sync your video again to apply the latest changes',
    es: 'Vuelve a generar el Lip-sync para aplicar los últimos cambios',
    pt: 'Refaça o Lip-sync para aplicar as últimas alterações',
    fr: 'Relancez le Lip-sync pour appliquer les dernières modifications',
    it: 'Ripeti il lip-sync per applicare le ultime modifiche',
    ru: 'Запустите липсинк снова, чтобы применить последние изменения',
  },
  'editor.video.dubFirstLipSync': {
    en: 'Dub your video first, then launch lip-sync',
    es: 'Dobla primero el video y luego inicia el Lip-sync',
    pt: 'Duble o vídeo antes de iniciar o Lip-sync',
    fr: 'Doublez d’abord la vidéo, puis lancez le Lip-sync',
    it: 'Doppia prima il video, poi avvia il lip-sync',
    ru: 'Сначала озвучьте видео, затем запустите липсинк',
  },
  'editor.video.dubFirstTranslated': {
    en: 'Dub your video first to download translated video',
    es: 'Dobla el video antes de descargarlo traducido',
    pt: 'Duble o vídeo antes de baixar a versão traduzida',
    fr: 'Doublez la vidéo avant de télécharger la version traduite',
    it: 'Doppia il video prima di scaricare la versione tradotta',
    ru: 'Сначала озвучьте видео, чтобы скачать перевод',
  },
  'editor.video.dubFirstViewTranslated': {
    en: 'Dub your video first to view the translated video',
    es: 'Dobla el video antes de ver la versión traducida',
    pt: 'Duble o vídeo antes de ver a versão traduzida',
    fr: 'Doublez la vidéo avant d’afficher la version traduite',
    it: 'Doppia il video prima di vedere la versione tradotta',
    ru: 'Сначала озвучьте видео, чтобы посмотреть переведённое видео',
  },
  'editor.video.downloadReady': {
    en: 'Video with subtitles is ready to download!',
    es: '¡El video con subtítulos está listo para descargar!',
    pt: 'O vídeo com legendas está pronto para baixar!',
    fr: 'La vidéo sous-titrée est prête à être téléchargée !',
    it: 'Il video con sottotitoli è pronto per il download!',
    ru: 'Видео с субтитрами готово к скачиванию!',
  },
  'editor.video.failedGenerate': {
    en: 'Failed to generate. Please try again',
    es: 'No se pudo generar. Inténtalo de nuevo',
    pt: 'Falha ao gerar. Tente novamente',
    fr: 'Échec de la génération. Réessayez',
    it: 'Generazione non riuscita. Riprova',
    ru: 'Не удалось создать. Попробуйте снова',
  },
  'editor.video.lipSyncFaceOnly': {
    en: 'Lip-sync is only available for videos with a human face',
    es: 'El Lip-sync solo está disponible para videos con un rostro humano',
    pt: 'O Lip-sync só está disponível para vídeos com um rosto humano',
    fr: 'Le Lip-sync est réservé aux vidéos montrant un visage humain',
    it: 'Il lip-sync è disponibile solo per video con un volto umano',
    ru: 'Липсинк доступен только для видео с лицом человека',
  },
  'editor.video.lipSyncFirstDownload': {
    en: 'Lip-sync your video first to download it',
    es: 'Genera primero el Lip-sync para descargar el video',
    pt: 'Faça o Lip-sync antes de baixar o vídeo',
    fr: 'Effectuez d’abord le Lip-sync pour télécharger la vidéo',
    it: 'Esegui prima il lip-sync per scaricare il video',
    ru: 'Сначала создайте липсинк, чтобы скачать видео',
  },
  'editor.video.lipSyncFirstView': {
    en: 'Lip-sync your video first to view the result',
    es: 'Aplica el Lip-sync a tu video primero para ver el resultado',
    pt: 'Faça primeiro o Lip-sync do vídeo para ver o resultado',
    fr: 'Lancez d’abord le Lip-sync pour voir le résultat',
    it: 'Esegui prima il lip-sync per vedere il risultato',
    ru: 'Сначала создайте липсинк, чтобы посмотреть результат',
  },
  'editor.video.lipSynced': {
    en: 'Lip-synced',
    es: 'Con Lip-sync',
    pt: 'Lip-sync',
    fr: 'Lip-sync',
    it: 'Lip-sync',
    ru: 'Липсинк',
  },
  'editor.video.original': {
    en: 'Original',
    es: 'Original',
    pt: 'Original',
    fr: 'Originale',
    it: 'Originale',
    ru: 'Оригинал',
  },
  'editor.video.translated': {
    en: 'Translated',
    es: 'Traducido',
    pt: 'Traduzido',
    fr: 'Traduite',
    it: 'Tradotto',
    ru: 'Перевод',
  },
  'editor.video.videoSubtitles': {
    en: 'Video with subtitles',
    es: 'Video con subtítulos',
    pt: 'Vídeo com legendas',
    fr: 'Vidéo sous-titrée',
    it: 'Video con sottotitoli',
    ru: 'Видео с субтитрами',
  },
  'editor.video.aiPresetLanguages': {
    en: 'Languages with AI voice presets only',
    es: 'Idiomas solo con voces de IA predefinidas',
    pt: 'Idiomas apenas com predefinições de voz de IA',
    fr: 'Langues avec uniquement des voix IA prédéfinies',
    it: 'Lingue con soli preset vocali IA',
    ru: 'Языки только с готовыми голосами ИИ',
  },
  'editor.video.audio': { en: 'Audio', es: 'Audio', pt: 'Áudio', fr: 'Audio', it: 'Audio', ru: 'Аудио' },
  'editor.video.audioBackground': {
    en: 'Audio with voice and background',
    es: 'Audio con voz y pista de fondo',
    pt: 'Áudio com voz e trilha de fundo',
    fr: 'Audio avec voix et fond sonore',
    it: 'Audio con voce e sottofondo',
    ru: 'Аудио с голосом и фоновой дорожкой',
  },
  'editor.video.audioVoice': {
    en: 'Audio with voice only',
    es: 'Audio solo con voz',
    pt: 'Áudio apenas com voz',
    fr: 'Audio avec voix uniquement',
    it: 'Audio con sola voce',
    ru: 'Аудио только с голосом',
  },
  'editor.video.checkingLipSync': {
    en: 'We’re checking whether your video can be lip-synced',
    es: 'Estamos comprobando si se puede aplicar Lip-sync al video',
    pt: 'Estamos verificando se o vídeo pode receber Lip-sync',
    fr: 'Nous vérifions si le Lip-sync est possible pour votre vidéo',
    it: 'Stiamo verificando se è possibile applicare il lip-sync al video',
    ru: 'Проверяем, можно ли применить липсинк к видео',
  },
  'editor.video.cloneLanguages': {
    en: 'Languages with voice clones and AI presets',
    es: 'Idiomas con clones de voz y voces de IA predefinidas',
    pt: 'Idiomas com clones de voz e predefinições de IA',
    fr: 'Langues avec clones vocaux et voix IA prédéfinies',
    it: 'Lingue con cloni vocali e preset IA',
    ru: 'Языки с клонированием голоса и готовыми голосами ИИ',
  },
  'editor.video.downloadAudioBackgroundAfterDub': {
    en: 'Dub your video first to download audio with voice and background',
    es: 'Dobla primero el video para descargar el audio con voz y pista de fondo',
    pt: 'Duble o vídeo antes de baixar o áudio com voz e trilha de fundo',
    fr: 'Doublez d’abord la vidéo pour télécharger l’audio avec voix et fond sonore',
    it: 'Doppia prima il video per scaricare l’audio con voce e sottofondo',
    ru: 'Сначала озвучьте видео, чтобы скачать аудио с голосом и фоновой дорожкой',
  },
  'editor.video.downloadAudioVoiceAfterDub': {
    en: 'Dub your video first to download audio with voice',
    es: 'Dobla primero el video para descargar el audio con voz',
    pt: 'Duble o vídeo antes de baixar o áudio com voz',
    fr: 'Doublez d’abord la vidéo pour télécharger l’audio avec la voix',
    it: 'Doppia prima il video per scaricare l’audio con la voce',
    ru: 'Сначала озвучьте видео, чтобы скачать аудио с голосом',
  },
  'editor.video.downloadCaptionsGenerate': {
    en: 'Click generate to download video with subtitles',
    es: 'Haz clic en Generar para descargar el video con subtítulos',
    pt: 'Clique em Gerar para baixar o vídeo com legendas',
    fr: 'Cliquez sur Générer pour télécharger la vidéo sous-titrée',
    it: 'Fai clic su Genera per scaricare il video con sottotitoli',
    ru: 'Нажмите «Создать», чтобы скачать видео с субтитрами',
  },
  'editor.video.downloadCaptionsLipSyncFirst': {
    en: 'Lip-sync your video first, then generate subtitles',
    es: 'Primero crea el Lip-sync y después genera los subtítulos',
    pt: 'Faça o Lip-sync do vídeo antes de gerar as legendas',
    fr: 'Appliquez d’abord le Lip-sync à la vidéo, puis générez les sous-titres',
    it: 'Esegui prima il lip-sync, poi genera i sottotitoli',
    ru: 'Сначала создайте липсинк, затем сгенерируйте субтитры',
  },
  'editor.video.downloadCaptionsDubFirst': {
    en: 'Dub your video first to generate subtitles',
    es: 'Dobla primero el video para generar subtítulos',
    pt: 'Duble o vídeo antes de gerar legendas',
    fr: 'Doublez d’abord la vidéo pour générer les sous-titres',
    it: 'Doppia prima il video per generare i sottotitoli',
    ru: 'Сначала озвучьте видео, чтобы сгенерировать субтитры',
  },
  'editor.video.downloadTranslatedSubtitlesAfterDub': {
    en: 'Dub your video first to download translated subtitles',
    es: 'Dobla primero el video para descargar los subtítulos traducidos',
    pt: 'Duble o vídeo antes de baixar as legendas traduzidas',
    fr: 'Doublez d’abord la vidéo pour télécharger les sous-titres traduits',
    it: 'Doppia prima il video per scaricare i sottotitoli tradotti',
    ru: 'Сначала озвучьте видео, чтобы скачать переведённые субтитры',
  },
  'editor.video.exportAfterSaving': {
    en: 'The transcription will be available to export after saving',
    es: 'La exportación de la transcripción estará disponible después de guardar',
    pt: 'A exportação da transcrição estará disponível após salvar',
    fr: 'L’exportation de la transcription sera disponible après l’enregistrement',
    it: 'L’esportazione della trascrizione sarà disponibile dopo il salvataggio',
    ru: 'Экспорт транскрипции станет доступен после сохранения',
  },
  'editor.video.exportTranslationAfterSaving': {
    en: 'The translation will be available to export after saving',
    es: 'La exportación de la traducción estará disponible después de guardar',
    pt: 'A exportação da tradução estará disponível após salvar',
    fr: 'L’exportation de la traduction sera disponible après l’enregistrement',
    it: 'L’esportazione della traduzione sarà disponibile dopo il salvataggio',
    ru: 'Экспорт перевода станет доступен после сохранения',
  },
  'editor.video.lipSyncAndSubtitles': {
    en: 'Video with lip-sync and subtitles',
    es: 'Video con Lip-sync y subtítulos',
    pt: 'Vídeo com Lip-sync e legendas',
    fr: 'Vidéo avec le Lip-sync et des sous-titres',
    it: 'Video con lip-sync e sottotitoli',
    ru: 'Видео с липсинком и субтитрами',
  },
  'editor.video.lipSyncReady': {
    en: 'Your lip-synced video is ready!',
    es: '¡Tu video con Lip-sync está listo!',
    pt: 'Seu vídeo com Lip-sync está pronto!',
    fr: 'Votre vidéo avec le Lip-sync est prête !',
    it: 'Il video con lip-sync è pronto!',
    ru: 'Видео с липсинком готово!',
  },
  'editor.video.lipSync': {
    en: 'Lip-sync',
    es: 'Lip-sync',
    pt: 'Lip-sync',
    fr: 'Lip-sync',
    it: 'Lip-sync',
    ru: 'Липсинк',
  },
  'editor.video.originalSubtitles': {
    en: 'Original subtitles with timecodes',
    es: 'Subtítulos originales con códigos de tiempo',
    pt: 'Legendas originais com marcações de tempo',
    fr: 'Sous-titres originaux avec horodatages',
    it: 'Sottotitoli originali con timecode',
    ru: 'Оригинальные субтитры с таймкодами',
  },
  'editor.video.originalTextSpeakers': {
    en: 'Original text segments with speakers',
    es: 'Segmentos de texto originales con hablantes',
    pt: 'Segmentos do texto original com identificação dos locutores',
    fr: 'Segments de texte originaux avec locuteurs',
    it: 'Segmenti di testo originali con locutori',
    ru: 'Оригинальные текстовые сегменты со спикерами',
  },
  'editor.video.originalVideo': {
    en: 'Original Video',
    es: 'Video original',
    pt: 'Vídeo original',
    fr: 'Vidéo originale',
    it: 'Video originale',
    ru: 'Оригинальное видео',
  },
  'editor.video.processingQueueFew': {
    en: 'Your video is processing. There are {count} lip-sync tasks in the queue',
    es: 'Tu video se está procesando. Hay {count} proyectos en curso',
    pt: 'Seu vídeo está sendo processado. Há {count} projetos em andamento',
    fr: 'Votre vidéo est en cours de traitement. {count} projets sont en cours',
    it: 'Il video è in elaborazione. Ci sono {count} progetti in corso',
    ru: 'Видео обрабатывается. В очереди {count} задачи липсинка',
  },
  'editor.video.processingQueueMany': {
    en: 'Your video is processing. There are {count} lip-sync tasks in the queue',
    es: 'Tu video se está procesando. Hay {count} proyectos en curso',
    pt: 'Seu vídeo está sendo processado. Há {count} projetos em andamento',
    fr: 'Votre vidéo est en cours de traitement. {count} projets sont en cours',
    it: 'Il video è in elaborazione. Ci sono {count} progetti in corso',
    ru: 'Видео обрабатывается. В очереди {count} задач липсинка',
  },
  'editor.video.processingQueueOne': {
    en: 'Your video is processing. There is {count} lip-sync task in the queue',
    es: 'Tu video se está procesando. Hay {count} proyecto en curso',
    pt: 'Seu vídeo está sendo processado. Há {count} projeto em andamento',
    fr: 'Votre vidéo est en cours de traitement. {count} projet est en cours',
    it: 'Il video è in elaborazione. C’è {count} progetto in corso',
    ru: 'Видео обрабатывается. В очереди {count} задача липсинка',
  },
  'editor.video.processingQueueOther': {
    en: 'Your video is processing. There are {count} lip-sync tasks in the queue',
    es: 'Tu video se está procesando. Hay {count} proyectos en curso',
    pt: 'Seu vídeo está sendo processado. Há {count} projetos em andamento',
    fr: 'Votre vidéo est en cours de traitement. {count} projets sont en cours',
    it: 'Il video è in elaborazione. Ci sono {count} progetti in corso',
    ru: 'Видео обрабатывается. В очереди {count} задачи липсинка',
  },
  'editor.video.processingSimple': {
    en: 'Your video is processing',
    es: 'Tu video se está procesando',
    pt: 'Seu vídeo está sendo processado',
    fr: 'Votre vidéo est en cours de traitement',
    it: 'Il video è in elaborazione',
    ru: 'Видео обрабатывается',
  },
  'editor.video.redubAudioBackground': {
    en: 'Redub your video to download audio with the background track',
    es: 'Vuelve a doblar el video para descargar el audio con la pista de fondo',
    pt: 'Duble o vídeo novamente para baixar o áudio com a trilha de fundo',
    fr: 'Doublez à nouveau la vidéo pour télécharger l’audio avec le fond sonore',
    it: 'Doppia di nuovo il video per scaricare l’audio con il sottofondo',
    ru: 'Озвучьте видео заново, чтобы скачать аудио с фоновой дорожкой',
  },
  'editor.video.textSubtitles': {
    en: 'Text subtitles (.srt)',
    es: 'Subtítulos de texto (.srt)',
    pt: 'Legendas em texto (.srt)',
    fr: 'Sous-titres au format texte (.srt)',
    it: 'File di sottotitoli (.srt)',
    ru: 'Текстовые субтитры (.srt)',
  },
  'editor.video.translatedLipSync': {
    en: 'Translated video with lip-sync',
    es: 'Video traducido con Lip-sync',
    pt: 'Vídeo traduzido com Lip-sync',
    fr: 'Vidéo traduite avec le Lip-sync',
    it: 'Video tradotto con lip-sync',
    ru: 'Переведённое видео с липсинком',
  },
  'editor.video.translatedSubtitles': {
    en: 'Translated video with subtitles',
    es: 'Video traducido con subtítulos',
    pt: 'Vídeo traduzido com legendas',
    fr: 'Vidéo traduite avec sous-titres',
    it: 'Video tradotto con sottotitoli',
    ru: 'Переведённое видео с субтитрами',
  },
  'editor.video.translatedSubtitlesTimecodes': {
    en: 'Translated subtitles with timecodes',
    es: 'Subtítulos traducidos con códigos de tiempo',
    pt: 'Legendas traduzidas com marcações de tempo',
    fr: 'Sous-titres traduits avec horodatages',
    it: 'Sottotitoli tradotti con timecode',
    ru: 'Переведённые субтитры с таймкодами',
  },
  'editor.video.translatedTextSpeakers': {
    en: 'Translated text segments with speakers',
    es: 'Segmentos de texto traducidos con hablantes',
    pt: 'Segmentos do texto traduzido com identificação dos locutores',
    fr: 'Segments de texte traduits avec locuteurs',
    it: 'Segmenti di testo tradotti con locutori',
    ru: 'Переведённые текстовые сегменты со спикерами',
  },
  'editor.video.translatedVideo': {
    en: 'Translated Video',
    es: 'Video traducido',
    pt: 'Vídeo traduzido',
    fr: 'Vidéo traduite',
    it: 'Video tradotto',
    ru: 'Переведённое видео',
  },
  'editor.video.video': { en: 'Video', es: 'Video', pt: 'Vídeo', fr: 'Vidéo', it: 'Video', ru: 'Видео' },
  'editor.video.videoLipSync': {
    en: 'Video with lip-sync',
    es: 'Video con Lip-sync',
    pt: 'Vídeo com Lip-sync',
    fr: 'Vidéo avec le Lip-sync',
    it: 'Video con lip-sync',
    ru: 'Видео с липсинком',
  },
  'editor.video.remakeSubtitle': {
    en: 'To generate lip-sync for the entire video and download it, simply relaunch the lip-sync feature.',
    es: 'Para generar el Lip-sync de todo el video y descargarlo, vuelve a iniciar la función de Lip-sync.',
    pt: 'Para gerar o Lip-sync do vídeo inteiro e baixá-lo, basta iniciar o Lip-sync novamente.',
    fr: 'Pour générer le Lip-sync de toute la vidéo et la télécharger, relancez simplement la fonction.',
    it: 'Per generare il lip-sync dell’intero video e scaricarlo, avvia nuovamente la funzione.',
    ru: 'Чтобы создать и скачать липсинк для всего видео, запустите функцию ещё раз.',
  },
  'editor.video.remakeTitle': {
    en: 'Start lip-sync again',
    es: 'Iniciar Lip-sync de nuevo',
    pt: 'Iniciar Lip-sync novamente',
    fr: 'Relancer le Lip-sync',
    it: 'Avvia di nuovo il lip-sync',
    ru: 'Запустить липсинк снова',
  },
} satisfies Record<string, TranslationRow>;

export type EditorTranslationKey = keyof typeof EDITOR_TRANSLATION_ROWS;
type EditorDictionaries = Record<CatalogLocale, Record<EditorTranslationKey, string>>;

const dictionaries = Object.fromEntries(
  CATALOG_LOCALES.map((locale) => [
    locale,
    Object.fromEntries(
      Object.entries(EDITOR_TRANSLATION_ROWS).map(([key, translations]) => [key, translations[locale]]),
    ),
  ]),
) as EditorDictionaries;

export const EDITOR_TRANSLATIONS = defineDomainTranslations(dictionaries);
